// Comprehensive service details for all CA services

export const serviceDetails = {
    'Proprietorship': {
        description: `A proprietorship is the simplest form of business structure where a single individual owns and operates the business. It's the most cost-effective way to start a business in India with minimal legal formalities. As a proprietor, you have complete control over your business decisions and operations.`,
        benefits: [
            'Easy and quick setup with minimal documentation',
            'Low cost of registration and maintenance',
            'Complete control over business decisions',
            'Simple taxation structure',
            'No separate tax filing for business',
            'Easy to close or transfer ownership',
            'Minimal compliance requirements',
            'Direct tax benefits available'
        ],
        documents: [
            'PAN Card of the proprietor',
            'Aadhaar Card of the proprietor',
            'Address proof (Electricity bill/Rent agreement)',
            'Bank account statement',
            'Passport size photographs',
            'Business address proof'
        ],
        process: [
            'Initial consultation and document collection',
            'Business name selection and verification',
            'GST registration (if applicable)',
            'Shop & Establishment Act registration',
            'Opening of current bank account',
            'Final documentation and handover'
        ],
        timeline: '3-7 working days',
        faqs: [
            {
                question: 'Is registration mandatory for proprietorship?',
                answer: 'Proprietorship registration is not mandatory, but GST registration becomes mandatory if turnover exceeds ₹40 lakhs (₹20 lakhs for service providers).'
            },
            {
                question: 'Can I have employees in a proprietorship?',
                answer: 'Yes, you can hire employees in a proprietorship firm. You need to comply with labor laws and obtain necessary registrations like ESI and PF if the number of employees exceeds the specified limit.'
            }
        ]
    },

    'Partnership': {
        description: `A partnership firm is a business structure where two or more individuals come together to run a business and share profits according to a partnership deed. It's governed by the Indian Partnership Act, 1932, and is ideal for businesses that require shared expertise and capital investment.`,
        benefits: [
            'Shared responsibility and workload among partners',
            'Combined skills, expertise, and resources',
            'Easier to raise capital compared to proprietorship',
            'More credibility than sole proprietorship',
            'Flexible profit-sharing arrangements',
            'Simple formation process',
            'Tax benefits for partners',
            'Easy decision-making among partners'
        ],
        documents: [
            'PAN Cards of all partners',
            'Aadhaar Cards of all partners',
            'Address proofs of all partners',
            'Passport size photographs of all partners',
            'Registered office address proof',
            'Rent agreement or ownership documents',
            'NOC from property owner (if rented)'
        ],
        process: [
            'Selection of partnership name',
            'Drafting of partnership deed',
            'Notarization of partnership deed',
            'PAN application for the firm',
            'Opening of partnership bank account',
            'GST registration (if applicable)',
            'Other necessary registrations'
        ],
        timeline: '5-10 working days',
        faqs: [
            {
                question: 'Is partnership deed registration mandatory?',
                answer: 'While registration is not mandatory, it is highly recommended as it provides legal validity and helps resolve disputes between partners.'
            },
            {
                question: 'How are profits shared in a partnership?',
                answer: 'Profits are shared as per the ratio mentioned in the partnership deed. If not specified, profits are shared equally among all partners.'
            }
        ]
    },

    'One Person Company': {
        description: `One Person Company (OPC) is a revolutionary concept introduced in the Companies Act, 2013, allowing a single person to operate a company with limited liability. It combines the benefits of a proprietorship and a private limited company, making it ideal for solo entrepreneurs who want to grow their business with corporate structure.`,
        benefits: [
            'Limited liability protection for the sole owner',
            'Separate legal entity status',
            'Perpetual succession regardless of owner changes',
            'Easy to raise funding and loans',
            'Higher credibility with clients and vendors',
            'Single person ownership and control',
            'Lower compliance compared to Pvt Ltd',
            'Can be converted to Pvt Ltd company later'
        ],
        documents: [
            'PAN Card of Director and Nominee',
            'Aadhaar Card of Director and Nominee',
            'Passport of Director (if available)',
            'Voter ID/Driving License',
            'Recent bank statement/utility bill',
            'Passport size photographs',
            'Digital signature certificate (DSC)',
            'Registered office address proof'
        ],
        process: [
            'Obtain Digital Signature Certificate (DSC)',
            'Director Identification Number (DIN) application',
            'Company name approval (RUN service)',
            'Filing of incorporation documents (SPICe+)',
            'MOA and AOA preparation and filing',
            'Certificate of Incorporation',
            'PAN and TAN allotment'
        ],
        timeline: '10-15 working days',
        faqs: [
            {
                question: 'Can an OPC have more than one director?',
                answer: 'No, an OPC can have only one director. However, you must appoint a nominee who will become the director in case of death or incapacity of the original director.'
            },
            {
                question: 'Can an NRI form an OPC?',
                answer: 'No, only Indian citizens and residents can form an OPC. NRIs, foreign nationals, and foreign companies cannot be members of an OPC.'
            }
        ]
    },

    'Limited Liability Partnership': {
        description: `Limited Liability Partnership (LLP) is a hybrid business structure that combines the benefits of partnership and company. It provides limited liability protection to partners while maintaining operational flexibility. LLPs are governed by the Limited Liability Partnership Act, 2008, and are increasingly popular among professionals and startups.`,
        benefits: [
            'Limited liability protection for all partners',
            'Separate legal entity independent of partners',
            'Perpetual succession',
            'No minimum capital requirement',
            'Less compliance compared to companies',
            'Flexible profit-sharing arrangement',
            'No requirement for audit if turnover < ₹40 lakhs',
            'Easy to add or remove partners'
        ],
        documents: [
            'PAN Cards of all designated partners',
            'Aadhaar Cards of all partners',
            'Passport (for foreign nationals)',
            'Address proof of registered office',
            'Rent agreement/ownership proof',
            'NOC from property owner',
            'Digital Signature Certificate (DSC)',
            'Passport size photographs'
        ],
        process: [
            'Obtain DSC for designated partners',
            'Apply for Director Identification Number (DIN)',
            'LLP name approval via RUN service',
            'Filing of FiLLiP form with documents',
            'LLP Agreement drafting and filing',
            'Certificate of Incorporation',
            'PAN and TAN application'
        ],
        timeline: '12-18 working days',
        faqs: [
            {
                question: 'What is the minimum number of partners required?',
                answer: 'An LLP must have a minimum of 2 designated partners. There is no maximum limit on the number of partners.'
            },
            {
                question: 'Can an LLP be converted to a Private Limited Company?',
                answer: 'Yes, an LLP can be converted into a Private Limited Company by following the prescribed legal procedure and complying with the Companies Act.'
            }
        ]
    },

    'Private Limited Company': {
        description: `Private limited company registration in India provides limited liability, legal independence, and access to tax benefits. Governed by the Companies Act, 2013, it requires a DSC, DIN, and documents like ID and address proof. The SPICe+ form enables combined application for name approval, incorporation, PAN, TAN, and GST. Once approved by the RoC, you receive a Certificate of Incorporation, allowing the company to operate legally, own assets, and sign contracts. We streamline the entire process by providing comprehensive end-to-end support for private limited company registration, legal documentation, and regulatory compliance.`,
        pricing: {
            standard: '₹1,499 + Govt. Fee',
            original: '₹2,999',
            timeline: '10-15 days',
            includes: [
                'Expert assisted process',
                'Name Approval through SPICe+ Part A',
                'DSC (Digital Signature Certificate) for 2 Directors',
                'DIN (Director Identification Number)',
                'MoA and AoA Drafting',
                'Share certificates issuance',
                'Company PAN + TAN',
                'Certificate of Incorporation'
            ]
        },
        benefits: [
            'Limited Liability - Personal assets of shareholders are protected',
            'Legal Independence - The company is a separate legal person in the eyes of the law',
            'Fundraising - Easy to raise capital from venture capitalists and angel investors',
            'Perpetual Succession - The company continues to exist even if owners change',
            'Tax Benefits - Access to various tax deductions and business expense claims',
            'Credibility - Higher brand value and trust among customers and vendors',
            'ESOPs - Ability to attract top talent by offering stock options'
        ],
        documentsRequired: {
            indianNationals: [
                'PAN Card (mandatory) for all directors/shareholders',
                'Identity Proof: Passport, Aadhar Card, Voter ID, or Driver’s License',
                'Address Proof: Recent Utility Bills or Bank Statements (not older than 2 months)',
                'Passport-size photographs'
            ],
            foreignNationals: [
                'Passport (mandatory)',
                'Address Proof: Driver’s License, Bank Statement, or Residence Card (notarized/apostilled)',
                'Passport-size photographs'
            ],
            registeredOffice: [
                'Recent Electricity Bill or Property Tax Receipt (within 30 days)',
                'Rent Agreement and No Objection Certificate (NOC) from the property owner'
            ],
            corporateShareholders: [
                'Board Resolution authorizing investment',
                'Incorporation Certificate of the investing company'
            ]
        },
        // Fallback flat list
        documents: [
            'PAN Card and Aadhar Card of directors',
            'Passport-size photos',
            'Address proof of business premises (Rent Agreement/NOC/Utility Bill)',
            'DSC for all directors',
            'Bank statements'
        ],
        process: [
            'Step 1: Obtain Digital Signature Certificate (DSC) - Required for all proposed directors for electronic signing.',
            'Step 2: Apply for Director Identification Number (DIN) - Mandatory unique number for each director.',
            'Step 3: Name Approval - Submit unique names via SPICe+ Part A for MCA approval.',
            'Step 4: Prepare Documents - Draft MoA, AoA, and gather ID/Address proofs.',
            'Step 5: File SPICe+ Part B - Complete incorporation filing with AGILE-PRO, eMOA, and eAOA.',
            'Step 6: PAN, TAN & GST - Auto-generated applications during the incorporation process.',
            'Step 7: Incorporation Certificate - RoC issues the Certificate of Incorporation (CIN) upon approval.',
            'Step 8: Post Compliance - Open bank account, issue share certificates, and commence business.'
        ],
        timeline: '10-15 working days',
        faqs: [
            {
                question: 'What is private limited company registration?',
                answer: 'Private limited company registration is a process that legally registers a business, offering limited liability, distinct identity, and capital-raising ability. Governed by the Companies Act, 2013, it is apt for startups and growing businesses.'
            },
            {
                question: 'How to register as a private limited company?',
                answer: 'To register, you need to obtain DSC and DIN for directors, apply for name approval, draft MoA/AoA, and file the SPICe+ form on the MCA portal with required documents.'
            },
            {
                question: 'Who is eligible for Pvt Ltd company?',
                answer: 'Any individual or corporate entity can register. You need a minimum of two directors (one Indian resident) and two shareholders.'
            },
            {
                question: 'What is the minimum capital requirement?',
                answer: 'There is no minimum paid-up capital requirement for a Private Limited Company. You can start with as little as ₹1.'
            },
            {
                question: 'Can a Private Limited Company operate multiple businesses?',
                answer: 'Yes, provided the Memorandum of Association (MoA) mentions the main objects of all diverse businesses you intend to undertake.'
            },
            {
                question: 'How long does it take to register?',
                answer: 'The process typically takes 10 to 15 working days, subject to government processing time and document correctness.'
            },
            {
                question: 'What is the SPICe+ form?',
                answer: 'SPICe+ (Simplified Proforma for Incorporating Company Electronically Plus) is an integrated web form offering 10 services (Name, DIN, Incorporation, PAN, TAN, GST, etc.) in a single application.'
            },
            {
                question: 'What Documents are required?',
                answer: 'Key documents include PAN and ID proof of directors, address proof of registered office (utility bill/rent agreement), and photographs. Foreign nationals need apostilled documents.'
            }
        ]
    },

    'Trust Registration': {
        description: `A Trust is a legal entity created for charitable, religious, or educational purposes. It's established through a Trust Deed and managed by trustees for the benefit of beneficiaries. Trust registration provides tax exemptions and legal recognition for social welfare activities.`,
        benefits: [
            'Tax exemption under Section 12AA and 80G',
            'Perpetual succession',
            'Limited liability for trustees',
            'Enhanced credibility for fundraising',
            'Legal recognition and protection',
            'Can receive foreign donations (with FCRA)',
            'Property can be held in trust name',
            'Separate legal entity'
        ],
        documents: [
            'Trust Deed on stamp paper',
            'PAN Cards of all trustees',
            'Aadhaar Cards of all trustees',
            'Address proof of registered office',
            'Photographs of trustees',
            'Objectives and aims of the trust',
            'List of trustees with addresses',
            'Property documents (if applicable)'
        ],
        process: [
            'Drafting of Trust Deed',
            'Registration on appropriate stamp paper',
            'Execution by all trustees',
            'Registration with Sub-Registrar',
            'PAN application for trust',
            '12A and 80G registration for tax exemption',
            'Bank account opening'
        ],
        timeline: '15-25 working days',
        faqs: [
            {
                question: 'What is the difference between Public and Private Trust?',
                answer: 'A Public Trust is created for the benefit of the general public, while a Private Trust is for specific individuals or family members. Public trusts get better tax benefits.'
            },
            {
                question: 'Can trustees receive salary?',
                answer: 'Yes, trustees can receive reasonable remuneration for their services, but it should be mentioned in the Trust Deed and should not be excessive.'
            }
        ]
    },

    'Society Registration': {
        description: `A Society is a voluntary association of people formed for promoting charitable, educational, cultural, or social causes. Registered under the Societies Registration Act, 1860, it's ideal for NGOs and community organizations working for social welfare.`,
        benefits: [
            'Tax exemptions under 12A, 80G, and 35AC',
            'Legal entity status',
            'Democratic governance structure',
            'Limited liability for members',
            'Easy to get grants and donations',
            'Can register under FCRA for foreign funding',
            'Perpetual existence',
            'Higher credibility for CSR funding'
        ],
        documents: [
            'Memorandum of Association (MOA)',
            'Rules and Regulations of Society',
            'List of managing committee members',
            'PAN and Aadhaar of all members',
            'Address proof of registered office',
            'Photographs of members',
            'Affidavit from members',
            'NOC from property owner'
        ],
        process: [
            'Name selection and verification',
            'Drafting of MOA and Rules',
            'Signature collection from members',
            'Notarization of documents',
            'Filing with Registrar of Societies',
            'Certificate of Registration',
            'PAN and 12A/80G registration'
        ],
        timeline: '20-30 working days',
        faqs: [
            {
                question: 'How many members are required?',
                answer: 'A minimum of 7 members are required to register a society. There is no maximum limit.'
            },
            {
                question: 'What is the difference between Society and Trust?',
                answer: 'A Society is a democratic body with voting rights for members, while a Trust is managed by trustees. Societies are better for membership-based organizations.'
            }
        ]
    },

    'Digital Signature': {
        description: `A Digital Signature Certificate (DSC) is the electronic equivalent of a physical signature used to sign documents digitally. It's mandatory for various online filings with MCA, Income Tax, GST, and other government portals. DSCs ensure authenticity, integrity, and non-repudiation of electronic documents.`,
        benefits: [
            'Legally valid for all online filings',
            'Secure document signing',
            'Time-saving and paperless process',
            'Prevents document tampering',
            'Mandatory for company incorporation',
            'Required for GST and IT returns filing',
            '2-year validity period',
            'Can be used across multiple platforms'
        ],
        documents: [
            'PAN Card of applicant',
            'Aadhaar Card',
            'Photograph (specification provided)',
            'Mobile number and email ID',
            'Address proof',
            'Request letter (for organization)',
            'Board resolution (for companies)'
        ],
        process: [
            'Selection of DSC class (Class 2 or 3)',
            'Document submission and verification',
            'Payment of DSC fees',
            'Video verification (if required)',
            'DSC issuance by Certifying Authority',
            'Token delivery via courier',
            'Installation and testing'
        ],
        timeline: '2-4 working days',
        faqs: [
            {
                question: 'What is the difference between Class 2 and Class 3 DSC?',
                answer: 'Class 2 DSC is used for IT returns and basic filings, while Class 3 DSC is required for company incorporation, e-tendering, and high-security applications.'
            },
            {
                question: 'Can one DSC be used for multiple purposes?',
                answer: 'Yes, a single DSC can be used for multiple purposes like MCA filings, IT returns, GST, import-export, and other government portals.'
            }
        ]
    },

    'Udyam Registration': {
        description: `Udyam Registration (formerly MSME/SSI Registration) is a government registration for micro, small, and medium enterprises. It provides numerous benefits including easier access to credit, subsidies, and government schemes. The registration is based on self-declaration with no requirement of documents or proof.`,
        benefits: [
            'Easier access to bank loans at lower interest rates',
            'Collateral-free credit facilities',
            'Protection against delayed payments',
            'Priority sector lending benefits',
            'Concession on electricity bills',
            'Exemption from stamp duty and registration fees',
            'Subsidies on patent registration',
            'Access to government tenders and schemes'
        ],
        documents: [
            'Aadhaar Number of proprietor/partner/director',
            'PAN of the business entity',
            'Business name and type',
            'Bank account details',
            'Investment and turnover details',
            'No other documents required (self-declaration)'
        ],
        process: [
            'Access Udyam Registration portal',
            'Enter Aadhaar and OTP verification',
            'Fill business details and classification',
            'Enter investment and turnover data',
            'Submit the form (no documents needed)',
            'Instant Udyam Registration Certificate',
            'Download certificate with unique number'
        ],
        timeline: 'Same day (instant registration)',
        faqs: [
            {
                question: 'What are the investment and turnover limits?',
                answer: 'Micro: Investment < ₹1 Cr & Turnover < ₹5 Cr | Small: Investment < ₹10 Cr & Turnover < ₹50 Cr | Medium: Investment < ₹50 Cr & Turnover < ₹250 Cr'
            },
            {
                question: 'Is it mandatory to have Udyam Registration?',
                answer: 'While not mandatory, it is highly beneficial for availing government schemes, subsidies, and bank loans at preferential rates.'
            }
        ]
    },

    'Startup India Registration': {
        description: `Startup India Registration is a flagship initiative by the Government of India to build a strong ecosystem for nurturing innovation and startups. Recognized startups get access to tax benefits, easier compliance, IPR fast-tracking, and funding opportunities. It's a must-have for tech startups and innovative businesses.`,
        benefits: [
            '100% tax exemption for 3 years',
            'Self-certification for 6 labor and 3 environmental laws',
            'Fast-track patent examination with 80% rebate',
            'Access to Fund of Funds (₹10,000 Cr corpus)',
            'Easy exit (90-day wind-up process)',
            'No angel tax on funding',
            'Hosting on Startup India portal',
            'Networking and mentorship opportunities'
        ],
        documents: [
            'Certificate of Incorporation/Registration',
            'Company/LLP PAN',
            'Brief about innovation and business model',
            'Website/Pitch deck (optional)',
            'Bank account details',
            'Directors/Partners details',
            'Recommendation letter (if applicable)'
        ],
        process: [
            'Incorporation of Pvt Ltd/LLP/Partnership',
            'Registration on Startup India portal',
            'Fill up the application form',
            'Upload required documents',
            'Submit innovative idea description',
            'DPIIT evaluation and approval',
            'Recognition certificate issuance'
        ],
        timeline: '30-45 working days',
        faqs: [
            {
                question: 'What is the eligibility criteria?',
                answer: 'Entity must be incorporated as Pvt Ltd/LLP/Partnership, be less than 10 years old, have annual turnover < ₹100 Cr, and work toward innovation/improvement of products or services.'
            },
            {
                question: 'Can a proprietorship register under Startup India?',
                answer: 'No, only Private Limited Companies, LLPs, and Partnership Firms are eligible for Startup India registration.'
            }
        ]
    },

    'FSSAI Registration': {
        description: `FSSAI (Food Safety and Standards Authority of India) registration/license is mandatory for every food business operator in India. Get your FSSAI license starting at ₹2,999 and operate your food business legally. Whether you're a manufacturer, trader, restaurant, or online food delivery, FSSAI ensures your business complies with food safety standards and builds customer trust.`,
        pricing: {
            basic: '₹2,999 (Turnover < ₹12 Lakhs)',
            state: '₹4,999 (Turnover ₹12L - ₹20 Cr)',
            central: '₹9,999 (Turnover > ₹20 Cr)',
            timeline: '15-60 days (depending on license type)',
            includes: [
                'Expert consultation on license type',
                'Form B preparation and filing',
                'Document verification assistance',
                'Follow-up with FSSAI authorities',
                'FSSAI certificate upon approval',
                'Guidance on compliance requirements'
            ]
        },
        licenseTypes: {
            basic: {
                name: 'FSSAI Basic Registration',
                turnover: 'Annual turnover < ₹12 Lakhs',
                validity: '1-5 years',
                price: '₹2,999'
            },
            state: {
                name: 'FSSAI State License',
                turnover: 'Annual turnover ₹12 Lakhs - ₹20 Crores',
                validity: '1-5 years',
                price: '₹4,999'
            },
            central: {
                name: 'FSSAI Central License',
                turnover: 'Annual turnover > ₹20 Crores or Importers/Exporters',
                validity: '1-5 years',
                price: '₹9,999'
            }
        },
        benefits: [
            'Legal Authorization - Mandatory to operate any food business in India',
            'Customer Trust - Builds credibility and confidence among customers',
            'Business Expansion - Required for selling on e-commerce platforms',
            'International Opportunities - Essential for food import/export',
            'Avoid Penalties - Prevents legal action and heavy fines',
            'Quality Assurance - Ensures food safety standards compliance',
            'Brand Recognition - FSSAI logo enhances brand value',
            'Nationwide Operations - Expand business across India'
        ],
        whoNeedsIt: [
            'Food manufacturers and processors',
            'Restaurants, cafes, and cloud kitchens',
            'Food traders and distributors',
            'Online food delivery platforms',
            'Home-based food businesses',
            'Food importers and exporters',
            'Catering services',
            'Food storage and warehousing'
        ],
        documents: [
            'Form B (for registration/license)',
            'Photo ID and address proof of proprietor/partners/directors',
            'Business incorporation certificate (for companies/LLPs)',
            'List of food products manufactured/sold',
            'Food safety management plan',
            'Source of water supply proof (water test report)',
            'Layout plan of processing unit with dimensions',
            'NOC from municipality/local body',
            'Rent agreement or ownership documents',
            'List of equipment and machinery'
        ],
        process: [
            'Determine FSSAI Type - Choose between Basic Registration, State License, or Central License based on turnover',
            'Document Preparation - Gather all required documents and prepare food safety plan',
            'Online Application - Fill Form B on FSSAI portal with business details',
            'Document Upload - Submit all supporting documents online',
            'Fee Payment - Pay the prescribed registration/license fee',
            'Inspection - Food safety officer inspection (if required for State/Central license)',
            'Approval - FSSAI reviews application and grants certificate',
            'Display License - Print and display FSSAI number on products and premises'
        ],
        timeline: '15-60 days (depending on license type)',
        faqs: [
            {
                question: 'What is the difference between FSSAI Registration and License?',
                answer: 'FSSAI Basic Registration is for small businesses with turnover < ₹12 lakhs. State License is for turnover between ₹12 lakhs - ₹20 Crores. Central License is for turnover > ₹20 Crores or for importers/exporters.'
            },
            {
                question: 'Is FSSAI mandatory for home-based food businesses?',
                answer: 'Yes, even home-based food businesses selling through online platforms or directly to customers need at least FSSAI Basic Registration.'
            },
            {
                question: 'How long does FSSAI registration take?',
                answer: 'Basic Registration takes 15-20 days. State License takes 30-45 days. Central License can take 45-60 days depending on inspection requirements.'
            },
            {
                question: 'What is the validity of FSSAI license?',
                answer: 'FSSAI license can be obtained for 1 to 5 years. You can choose the validity period while applying and pay fees accordingly.'
            },
            {
                question: 'Can I sell on Swiggy/Zomato without FSSAI?',
                answer: 'No, FSSAI registration is mandatory to list your restaurant or cloud kitchen on any food delivery platform like Swiggy, Zomato, or Uber Eats.'
            },
            {
                question: 'What is the penalty for operating without FSSAI?',
                answer: 'Operating without FSSAI license can attract a penalty up to ₹5 lakhs and/or imprisonment up to 6 months. The food business can also be shut down.'
            },
            {
                question: 'Do I need separate FSSAI for multiple outlets?',
                answer: 'Yes, you need separate FSSAI registration/license for each manufacturing unit or outlet at different locations.'
            }
        ]
    },

    'Professional Tax': {
        description: `Professional Tax is a state-level tax levied on income earned from profession, trade, calling, or employment. It's mandatory for all salaried individuals, businesses, and professionals whose income exceeds the prescribed limit. The tax amount and rules vary from state to state.`,
        benefits: [
            'Legal compliance with state tax laws',
            'Tax deduction allowed in income tax',
            'Avoid penalties and legal issues',
            'Required for business registration',
            'Necessary for government tenders',
            'Professional credibility',
            'Employer compliance for deducting from salaries',
            'State-wise provisions available'
        ],
        documents: [
            'Application form (state-specific)',
            'PAN Card of business/professional',
            'Business registration certificate',
            'Address proof of business premises',
            'Identity proof of applicant',
            'Rent agreement (if applicable)',
            'Employee details (for employers)',
            'Bank account details'
        ],
        process: [
            'Identify applicable state and rules',
            'Fill registration application form',
            'Submit required documents',
            'Payment of registration fees',
            'Receive Professional Tax certificate',
            'Monthly/annual PT payment as per schedule',
            'Annual return filing'
        ],
        timeline: '7-15 working days',
        faqs: [
            {
                question: 'Which states levy Professional Tax?',
                answer: 'Most states levy PT except for Delhi, Haryana, Rajasthan, UP, Uttarakhand, HP, J&K, and Punjab. The tax amount varies from ₹200 to ₹2,500 annually.'
            },
            {
                question: 'Is Professional Tax deductible from income tax?',
                answer: 'Yes, Professional Tax paid is allowed as a deduction under Section 16(iii) of the Income Tax Act from salary income.'
            }
        ]
    },

    'Darpan Registration': {
        description: `DARPAN (Digital Advancement of Rural Post Office for A New India) is an online portal for registration of NGOs/VOs. It's mandatory for organizations seeking grants from NITI Aayog and other government bodies. The registration provides a unique ID and makes NGOs eligible for CSR funding and government schemes.`,
        benefits: [
            'Eligibility for NITI Aayog grants',
            'Access to CSR funding opportunities',
            'Listed in government NGO database',
            'Increased credibility and visibility',
            'Easy verification by donors',
            'Registration process',
            'Required for government collaborations',
            'Transparent rating system'
        ],
        documents: [
            'Registration certificate of NGO/Trust/Society',
            'PAN Card of organization',
            '80G and 12A certificates (if available)',
            'Audited financial statements (3 years)',
            'List of governing body members',
            'Details of key projects/activities',
            'Bank account details',
            'Contact information'
        ],
        process: [
            'Visit NITI Aayog NGO-DARPAN portal',
            'Create organization account',
            'Fill registration form with details',
            'Upload all required documents',
            'Submit application for verification',
            'Receive unique DARPAN ID',
            'Update information annually'
        ],
        timeline: '5-10 working days',
        faqs: [
            {
                question: 'Who needs DARPAN registration?',
                answer: 'All NGOs, societies, and trusts seeking government grants, CSR funding, or collaborations with NITI Aayog and other government bodies need DARPAN registration.'
            },
            {
                question: 'Are there any charges for DARPAN registration?',
                answer: 'No, DARPAN registration has no charges for registration or renewal.'
            }
        ]
    },

    'RCMC Registration': {
        description: `Registration cum Membership Certificate (RCMC) is mandatory for exporters to avail benefits under various export promotion schemes. Issued by Export Promotion Councils (EPCs), RCMC enables businesses to access subsidies, duty drawback, and other export incentives from the government.`,
        benefits: [
            'Eligibility for export incentive schemes',
            'Access to duty drawback benefits',
            'Merchandise Exports from India Scheme (MEIS) benefits',
            'Authorization for export documentation',
            'Networking with international buyers',
            'Market information and trade leads',
            'Participation in trade fairs and exhibitions',
            'Export credit at competitive rates'
        ],
        documents: [
            'Import Export Code (IEC)',
            'Company registration certificate',
            'PAN Card of the business',
            'GST registration certificate',
            'Bank certificate with IFS code',
            'Cancelled cheque of current account',
            'Details of directors/partners',
            'Photographs of authorized signatory'
        ],
        process: [
            'Identify relevant Export Promotion Council',
            'Apply for IEC (if not already done)',
            'Fill RCMC application form',
            'Submit required documents',
            'Pay membership and processing fees',
            'Verification by EPC',
            'RCMC certificate issuance (1-year validity)'
        ],
        timeline: '10-20 working days',
        faqs: [
            {
                question: 'Which products need RCMC?',
                answer: 'RCMC is product-specific. You need to apply to the relevant Export Promotion Council based on your product category (e.g., Apparel EPC, Engineering EPC, Chemical EPC, etc.).'
            },
            {
                question: 'What is the validity of RCMC?',
                answer: 'RCMC is valid for one year from the date of issuance and needs to be renewed annually.'
            }
        ]
    },

    'Halal Certificate': {
        description: `Halal Certification is essential for food and pharmaceutical products to be exported to Islamic countries. The certificate ensures that products comply with Islamic dietary laws and are permissible for consumption by Muslims. It opens up massive export opportunities in Middle East and Southeast Asian markets.`,
        benefits: [
            'Access to 1.8 billion Muslim consumers worldwide',
            'Mandatory for exports to Islamic countries',
            'Increases product acceptability and trust',
            'Premium pricing in Halal markets',
            'Competitive advantage in global trade',
            'Regulatory compliance in Muslim countries',
            'Brand reputation enhancement',
            'Higher profit margins'
        ],
        documents: [
            'Company registration certificate',
            'FSSAI license/registration',
            'Product label and packaging details',
            'List of ingredients and sources',
            'Manufacturing process flowchart',
            'Supplier certificates for raw materials',
            'Factory layout and hygiene details',
            'ISO/GMP certificates (if available)'
        ],
        process: [
            'Application to Halal certification body',
            'Document submission and verification',
            'Factory inspection and audit',
            'Product testing and verification',
            'Source verification of ingredients',
            'Certification committee approval',
            'Halal certificate issuance (1-year validity)'
        ],
        timeline: '30-60 working days',
        faqs: [
            {
                question: 'Which products need Halal certification?',
                answer: 'Food products, pharmaceuticals, cosmetics, and personal care items exported to or sold in Islamic countries typically need Halal certification.'
            },
            {
                question: 'Is Halal certification mandatory in India?',
                answer: 'No, it\'s not mandatory in India but is essential for exporting to Muslim-majority countries and for catering to Muslim consumers.'
            }
        ]
    },

    'Import Export Code': {
        description: `Import Export Code (IEC) is a 10-digit unique code issued by DGFT (Directorate General of Foreign Trade) mandatory for import or export of goods and services. Without IEC, no person/entity can import or export goods except for specified categories. It's a lifetime registration with no renewal requirement.`,
        benefits: [
            'Legal authorization for import-export business',
            'Lifetime validity (no renewal needed)',
            'PAN-based unique identification',
            'Required for customs clearance',
            'Eligibility for export incentives',
            'Access to international markets',
            'Opening foreign currency accounts',
            'Government export benefits'
        ],
        documents: [
            'PAN Card of applicant/entity',
            'Aadhaar Card (for proprietorship)',
            'Company registration certificate',
            'Bank certificate on letterhead',
            'Cancelled cheque of current account',
            'Photograph of proprietor/partners/directors',
            'Digital signature (for online filing)',
            'Address proof of business premises'
        ],
        process: [
            'Apply online on DGFT portal',
            'Fill Aayaat Niryaat Form (ANF2A)',
            'Upload required documents',
            'Digital signature verification',
            'Payment of application fee',
            'Verification by DGFT',
            'IEC allotment (digital certificate)'
        ],
        timeline: '5-10 working days',
        faqs: [
            {
                question: 'Is IEC required for service exports?',
                answer: 'Yes, IEC is required for both goods and services exports. However, services provided to Indian entities don\'t need IEC.'
            },
            {
                question: 'Can IEC be obtained for proprietorship?',
                answer: 'Yes, IEC can be obtained for proprietorship, partnership, LLP, and company. For proprietorship, it\'s issued in the name of the proprietor with firm name.'
            }
        ]
    },

    // ISO Standards & Certifications
    'ISO 9001:2015': {
        description: `ISO 9001:2015 (Quality Management System) is the world’s leading quality management standard, applicable to any organization to help meet statutory and regulatory requirements and achieve excellence in customer service. It focuses on continuous improvement and customer satisfaction.`,
        benefits: [
            'Improved customer satisfaction and loyalty',
            'Consistent product/service quality',
            'Operational efficiency and cost reduction',
            'International recognition and credibility',
            'Better risk management',
            'Employee engagement and motivation',
            'Compliance with legal requirements'
        ],
        documents: [
            'Business Registration Proof',
            'Scope of Activity',
            'Quality Manual (if applicable)',
            'Standard Operating Procedures (SOPs)',
            'Internal Audit Reports',
            'Management Review Minutes'
        ],
        process: [
            'Gap Analysis',
            'Documentation and Implementation',
            'Internal Audit',
            'Management Review',
            'Certification Audit (Stage 1 & Stage 2)',
            'Issuance of ISO 9001 Certificate'
        ],
        timeline: '15-30 days',
        faqs: [
            {
                question: "What is ISO 9001 and why is it important?",
                answer: "ISO 9001 is a globally recognized standard for quality management. It helps businesses ensure they meet customer needs and improve processes continuously."
            }
        ]
    },
    'ISO 14001:2015': {
        description: `ISO 14001:2015 (Environmental Management System) provides a systematic framework to manage immediate and long-term environmental impacts of products, services, and processes. It helps organizations improve their environmental performance through more efficient use of resources and reduction of waste.`,
        benefits: [
            'Reduced environmental footprint',
            'Cost savings through energy/waste reduction',
            'Compliance with environmental laws',
            'Improved corporate image',
            'New business opportunities with green clients'
        ],
        documents: ['Environmental Policy', 'Aspect-Impact Analysis', 'Legal Register', 'Emergency Preparedness Plan'],
        process: ['Environmental Review', 'System Design', 'Implementation', 'Internal Audit', 'External Certification Audit'],
        timeline: '20-45 days',
        faqs: [{ question: "Is ISO 14001 mandatory?", answer: "It is voluntary but increasingly required by clients and regulators for demonstrating environmental responsibility." }]
    },
    'ISO 45001:2018': {
        description: `ISO 45001:2018 (Occupational Health & Safety Management System) focuses on improving employee safety, reducing workplace risks, and creating better, safer working conditions. It is the first global standard for OHS.`,
        benefits: ['Reduced workplace incidents', 'Lower insurance premiums', 'Enhanced employee safety culture', 'Legal compliance'],
        documents: ['OHS Policy', 'Hazard Identification & Risk Assessment', 'Incident Investigation Records'],
        process: ['Gap Analysis', 'Risk Assessment', 'Control Implementation', 'Audit', 'Certification'],
        timeline: '20-45 days',
        faqs: [{ question: "Does it replace OHSAS 18001?", answer: "Yes, ISO 45001 has replaced OHSAS 18001 as the global standard for occupational health and safety." }]
    },
    'ISO 27001:2013': {
        description: `ISO 27001:2013 (Information Security Management System) is the gold standard for information security. It helps organizations protect their information assets (data) and manage the security of assets entrusted to them by third parties.`,
        benefits: ['Protection of sensitive data', 'Compliance with GDPR/Data laws', 'Customer trust in data handling', 'Risk management'],
        documents: ['ISMS Policy', 'Statement of Applicability (SoA)', 'Risk Treatment Plan', 'Asset Register'],
        process: ['Scope Definition', 'Risk Assessment', 'Control implementation', 'Internal Audit', 'Certification Audit'],
        timeline: '30-60 days',
        faqs: [{ question: "Is it suitable for small businesses?", answer: "Yes, ISO 27001 is scalable and critical for any business handling sensitive customer data." }]
    },
    'ISO 22000:2018': {
        description: `ISO 22000:2018 (Food Safety Management Systems) maps out what an organization needs to do to demonstrate its ability to control food safety hazards in order to ensure that food is safe. It applies to any organization in the food chain.`,
        benefits: ['Ensured food safety', 'Global market access', 'Compliance with HACCP principles', 'Reduced foodborne risks'],
        documents: ['FSMS Manual', 'HACCP Plan', 'Prerequisite Programs (PRPs)', 'Traceability Records'],
        process: ['HACCP Study', 'System Implementation', 'Internal Audit', 'Stage 1 & 2 Audits'],
        timeline: '30-45 days',
        faqs: [{ question: "How is it different from FSSAI?", answer: "FSSAI is a mandatory Indian license, while ISO 22000 is an international voluntary standard for food safety management systems." }]
    },
    'FSSC 22000': {
        description: `FSSC 22000 offers a robust, ISO-based certification scheme for the audit and certification of Food Safety Management Systems. It is recognized by the Global Food Safety Initiative (GFSI).`,
        benefits: ['GFSI Recognition', 'Accepted by global retailers', 'Comprehensive food safety framework'],
        documents: ['ISO 22000 docs', 'Sector specific PRPs', 'FSSC specific requirements'],
        process: ['System Development', 'Implementation', 'Certification Audit'],
        timeline: '45-60 days',
        faqs: [{ question: "Is FSSC 22000 better than ISO 22000?", answer: "FSSC 22000 includes ISO 22000 plus additional requirements, making it GFSI recognized and preferred by major global retailers." }]
    },
    'ISO 13485:2016': {
        description: `ISO 13485:2016 (Medical Devices QMS) specifies requirements for a quality management system where an organization needs to demonstrate its ability to provide medical devices and related services that consistently meet customer and applicable regulatory requirements.`,
        benefits: ['Regulatory compliance for medical devices', 'Risk management in product lifecycle', 'Enhanced product safety'],
        documents: ['Quality Manual', 'Device Master File', 'Risk Management File (ISO 14971)'],
        process: ['QMS Setup', 'Risk Management', 'Internal Audit', 'Certification'],
        timeline: '45-90 days',
        faqs: [{ question: "Is it mandatory for medical device manufacturers?", answer: "It is often a regulatory requirement or effectively mandatory for market access in many jurisdictions." }]
    },
    'IATF 16949:2016': {
        description: `IATF 16949:2016 is the global technical specification and quality management standard for the automotive industry. It emphasizes defect prevention and contribution to the reduction of variation and waste in the automotive supply chain.`,
        benefits: ['Automotive supply chain entry', 'Defect prevention', 'Continual improvement'],
        documents: ['Quality Manual', 'Control Plans', 'FMEA', 'MSA Studies'],
        process: ['Gap Analysis', 'Core Tools Implementation', 'Full Cycle Audit', 'Certification'],
        timeline: '3-6 months',
        faqs: [{ question: "Can I get IATF without ISO 9001?", answer: "IATF 16949 is implemented in conjunction with ISO 9001, not as a standalone standard." }]
    },
    'ROHS Compliance': {
        description: 'RoHS (Restriction of Hazardous Substances) compliance ensures that electrical and electronic products do not contain specific hazardous materials above agreed levels.',
        benefits: ['Market access to EU', 'Environmental responsibility', 'Safer products'],
        documents: ['Bill of Materials', 'Test Reports', 'Declaration of Conformity'],
        process: ['Product Assessment', 'Testing', 'Documentation', 'Compliance Declaration'],
        timeline: '2-4 weeks',
        faqs: []
    },
    'CE Marking': {
        description: 'CE Marking indicates conformity with health, safety, and environmental protection standards for products sold within the European Economic Area (EEA).',
        benefits: ['Access to European market', 'Product safety assurance', 'Consumer confidence'],
        documents: ['Technical File', 'Risk Assessment', 'Test Reports', 'EU Declaration of Conformity'],
        process: ['Identify Directives', 'Conformity Assessment', 'Technical Documentation', 'Affix CE Mark'],
        timeline: '4-8 weeks',
        faqs: []
    },

    'Trade License': {
        description: `Trade License is a mandatory license issued by the Municipal Corporation or local authority to carry out any trade or business activity. It ensures that the business complies with local laws, maintains hygiene standards, and doesn't pose a threat to public health and safety. Every business establishment needs a trade license.`,
        benefits: [
            'Legal authorization to operate business',
            'Compliance with municipal regulations',
            'Avoids penalties and business closure',
            'Required for bank loans and funding',
            'Necessary for other business registrations',
            'Professional credibility',
            'Protection from legal issues',
            'Required for property tax benefits'
        ],
        documents: [
            'Trade license application form',
            'Identity and address proof of applicant',
            'Business registration certificate',
            'PAN Card and Aadhaar Card',
            'Partnership deed/MOA-AOA (if applicable)',
            'Property documents/Rent agreement',
            'NOC from property owner',
            'Blueprint of business premises',
            'Fire safety certificate (for certain businesses)'
        ],
        process: [
            'Apply to local Municipal Corporation',
            'Fill prescribed application form',
            'Submit required documents',
            'Pay application and license fees',
            'Inspection by municipal inspector',
            'Compliance verification',
            'Trade license issuance (yearly renewal)'
        ],
        timeline: '15-30 working days',
        faqs: [
            {
                question: 'Which businesses need a Trade License?',
                answer: 'All businesses including retail shops, restaurants, manufacturing units, offices, and service providers need a trade license from the local municipal authority.'
            },
            {
                question: 'What is the validity of a Trade License?',
                answer: 'Trade licenses are typically valid for one year and need to be renewed annually before expiry.'
            }
        ]
    },

    'GST Registration': {
        description: `Goods and Services Tax (GST) Registration is mandatory for businesses with turnover exceeding specified limits. Get your GST Number in 4 days with expert assistance. GST registration provides a unique 15-digit GSTIN, enables input tax credit claims, and is essential for legal business operations across India.`,
        pricing: {
            standard: '₹499',
            original: '₹999',
            timeline: '48 hours filing, 4 days for GSTIN',
            includes: [
                'Expert GST Form Filing',
                'GST Certificate (upon government approval)',
                'Expert Consultation',
                'Document verification assistance'
            ]
        },
        benefits: [
            'Input Tax Credit - Claim credit for taxes paid on purchases',
            'Legal Recognition - Legally recognized as a supplier of goods/services',
            'Interstate Sales - Sell goods/services across state borders without restrictions',
            'E-commerce Access - Mandatory for selling on platforms like Amazon/Flipkart',
            'Enhanced business credibility and professional image',
            'Nationwide business operations',
            'Mandatory for government tenders',
            'Composition scheme benefits (if eligible)'
        ],
        thresholdLimits: {
            title: 'Who Should Apply for GST Registration?',
            categories: [
                {
                    type: 'Service Providers',
                    limit: 'Aggregate turnover > ₹20 Lakhs (₹10 Lakhs for Special Category States)'
                },
                {
                    type: 'Goods Suppliers',
                    limit: 'Aggregate turnover > ₹40 Lakhs'
                },
                {
                    type: 'Interstate Business',
                    limit: 'Mandatory regardless of turnover'
                },
                {
                    type: 'E-commerce Sellers',
                    limit: 'Mandatory for selling through platforms'
                }
            ]
        },
        documents: [
            'PAN Card & Aadhaar Card of proprietor/partners/directors',
            'Passport-sized photograph',
            'Bank account details (Cancelled cheque or bank statement)',
            'Address Proof (Electricity bill, Rent agreement, or NOC from property owner)',
            'Business registration certificate (for companies/LLPs)',
            'Digital signature or E-sign',
            'Authorization letter and board resolution (for companies)'
        ],
        process: [
            'Expert Consultation - Connect with a GST expert to understand requirements',
            'Document Submission - Upload digital copies of required documents',
            'Application Filing - Professionals file the GST REG-01 form on the portal',
            'GSTIN Receipt - Receive your GST number and certificate after government verification'
        ],
        timeline: '3-7 working days',
        commonMistakes: [
            'Incorrect Documentation (mismatch in names/addresses)',
            'Inaccurate Invoice Details',
            'Missed Deadlines for Return Filing',
            'Wrong selection of GST Composition Scheme'
        ],
        faqs: [
            {
                question: 'How long does GST registration take?',
                answer: 'Usually 3-7 working days. With our expert filing service, your application is filed within 48 hours, and you receive your GSTIN in 4 days on average.'
            },
            {
                question: 'Is GST registration mandatory for everyone?',
                answer: 'GST registration is mandatory only if turnover exceeds ₹40 lakhs (goods) or ₹20 lakhs (services). However, it\'s mandatory for interstate business and e-commerce sellers regardless of turnover.'
            },
            {
                question: 'Can I register for GST voluntarily?',
                answer: 'Yes, you can register voluntarily to claim Input Tax Credit and increase business credibility, even if your turnover is below the threshold limit.'
            },
            {
                question: 'What is the GST composition scheme?',
                answer: 'The composition scheme is for small taxpayers with turnover up to ₹1.5 crore. It offers a simplified compliance mechanism with lower tax rates but no input tax credit.'
            },
            {
                question: 'What documents are required for GST registration?',
                answer: 'You need PAN Card, Aadhaar Card, passport photo, bank details, and address proof. For companies, additional documents like incorporation certificate and board resolution are required.'
            }
        ]
    },

    'GST Filing': {
        description: `GST Filing involves submitting regular returns to the GST portal reporting sales, purchases, and tax liability. Different types of returns (GSTR-1, GSTR-3B, GSTR-9) need to be filed monthly, quarterly, or annually based on business type and turnover. Timely filing avoids penalties and keeps businesses compliant.`,
        benefits: [
            'Compliance with GST laws',
            'Claim input tax credit',
            'Avoid penalties and interest',
            'Maintain clean business record',
            'Required for annual return filing',
            'Helps in business expansion',
            'Government tender eligibility',
            'Professional business practices'
        ],
        documents: [
            'Sales invoices and bills',
            'Purchase invoices',
            'Debit and credit notes',
            'Bank statements',
            'Previous GST returns filed',
            'Import-export documents (if applicable)',
            'E-way bills generated',
            'Reconciliation statements'
        ],
        process: [
            'Prepare sales and purchase data',
            'Login to GST portal with credentials',
            'Fill GSTR-1 (outward supplies)',
            'Verify and submit GSTR-3B',
            'Pay tax liability through challan',
            'Download filed return acknowledgement',
            'Maintain records for audit'
        ],
        timeline: 'Monthly/Quarterly as per due dates',
        faqs: [
            {
                question: 'What returns do I need to file?',
                answer: 'Regular taxpayers file GSTR-1 (sales), GSTR-3B (summary return) monthly, and GSTR-9 (annual return). Composition dealers file GSTR-4 quarterly.'
            },
            {
                question: 'What happens if I miss the filing deadline?',
                answer: 'Late filing attracts late fees of ₹50/day (₹20/day for nil returns) per act, maximum ₹5,000. Interest is charged @18% p.a. on tax amount.'
            }
        ]
    },

    'LUT Filing Under GST': {
        description: `Letter of Undertaking (LUT) is a declaration filed by exporters to export goods/services without paying IGST. Instead of paying tax and claiming refund later, LUT allows zero-rated supply for exports. It's valid for one financial year and must be filed annually before making any exports.`,
        benefits: [
            'Export without paying IGST',
            'Improved cash flow for exporters',
            'No refund hassles',
            'Time-saving process',
            'Competitive pricing in international market',
            'Operational convenience',
            'Valid for entire financial year',
            'Online submission'
        ],
        documents: [
            'GSTIN of the exporter',
            'Previous year GST returns',
            'Import Export Code (IEC)',
            'Bank account details',
            'Digital signature certificate',
            'Board resolution (for companies)',
            'Self-declaration',
            'Previous LUT copy (for renewal)'
        ],
        process: [
            'Login to GST portal',
            'Navigate to Services > User Services > Furnish LUT',
            'Fill Form GST RFD-11',
            'Verify all details',
            'Submit with DSC/E-sign',
            'Download acknowledgement',
            'Use for zero-rated exports'
        ],
        timeline: 'Same day (instant)',
        faqs: [
            {
                question: 'Who can file LUT?',
                answer: 'Exporters with good compliance record (no proceeding or offence pending) can file LUT. New exporters can also file LUT from the first year.'
            },
            {
                question: 'What is the validity of LUT?',
                answer: 'LUT is valid for one financial year (April to March) and needs to be filed afresh at the beginning of each financial year.'
            }
        ]
    },

    'GST Cancellation': {
        description: `GST Cancellation is the process of surrendering GST registration when business is closed, discontinued, or no longer required. It can be voluntary (by taxpayer) or suo-moto (by department). Proper cancellation ensures no future compliance burden and protects from penalties for non-filing.`,
        benefits: [
            'No future compliance requirements',
            'Avoid penalties for non-filing',
            'Clean closure of business',
            'No liability for future tax',
            'Professional exit from GST system',
            'Required for business restructuring',
            'Prevents misuse of GSTIN',
            'Legal closure documentation'
        ],
        documents: [
            'GST registration certificate',
            'Stock register/inventory details',
            'All pending GST returns filed',
            'Final GSTR-10 (within 3 months)',
            'Tax payment proof',
            'Reason for cancellation',
            'Surrender of unused invoices',
            'Digital signature certificate'
        ],
        process: [
            'File all pending GST returns',
            'Clear all tax dues',
            'Login to GST portal',
            'Apply for cancellation (GST REG-16)',
            'Upload required documents',
            'Submit application',
            'File GSTR-10 (final return)',
            'Receive cancellation order'
        ],
        timeline: '15-30 working days',
        faqs: [
            {
                question: 'Can I cancel GST voluntarily?',
                answer: 'Yes, you can apply for voluntary cancellation if you close your business, transfer business, or turnover falls below threshold and you don\'t want to continue.'
            },
            {
                question: 'What is GSTR-10?',
                answer: 'GSTR-10 is the final return that must be filed within 3 months of cancellation date, declaring closing stock and other details as on the date of cancellation.'
            }
        ]
    },

    'Accounting': {
        description: `Professional accounting and bookkeeping services ensure accurate financial record-keeping, compliance with accounting standards, and timely financial reporting. From day-to-day transaction recording to financial statement preparation, we provide comprehensive accounting solutions for businesses of all sizes.`,
        benefits: [
            'Accurate financial records',
            'Compliance with accounting standards',
            'Timely financial statements',
            'Better business decision making',
            'Tax planning and savings',
            'Audit readiness',
            'Cash flow management',
            'Professional financial reporting',
            'Statutory compliance',
            'Cost optimization'
        ],
        documents: [
            'Business registration documents',
            'Bank statements',
            'Sales and purchase invoices',
            'Expense bills and receipts',
            'Salary and payroll records',
            'Asset and liability details',
            'Previous financial statements',
            'Tax payment challans',
            'Loan and investment documents'
        ],

        process: [
            'Initial consultation and requirement gathering',
            'Chart of accounts setup',
            'Daily transaction recording',
            'Bank reconciliation',
            'Expense categorization',
            'Monthly financial reporting',
            'Year-end closing entries',
            'Financial statements preparation'
        ],
        timeline: 'Ongoing monthly service',
        faqs: [
            {
                question: 'What is the difference between bookkeeping and accounting?',
                answer: 'Bookkeeping is the systematic recording of daily transactions. Accounting involves analyzing, interpreting, and presenting financial data for decision-making.'
            },
            {
                question: 'Do I need an accountant for a small business?',
                answer: 'Yes, even small businesses benefit from professional accounting for accurate GST filing, ITR filing, and maintaining statutory records.'
            }
        ]
    },

    'TDS Return Filing': {
        description: `Tax Deducted at Source (TDS) return filing is mandatory for individuals/businesses who deduct tax at source on payments like salary, rent, professional fees, etc. TDS returns (24Q, 26Q, 27Q, 27EQ) must be filed quarterly. We ensure accurate TDS computation, challan payment, and timely return filing.`,
        benefits: [
            'Compliance with TDS provisions',
            'Avoid TDS penalties and interest',
            'Issue Form 16/16A to deductees',
            'Maintain good compliance record',
            'Required for higher value transactions',
            'Proper credit to deductees',
            'Professional tax management',
            'Audit requirement fulfillment'
        ],
        documents: [
            'TAN of the deductor',
            'Payment details (salary, rent, professional fees)',
            'PAN of deductees',
            'TDS challan payment proof (Form 26AS)',
            'Deductee bank account details',
            'Previous TDS returns',
            'Correction statements (if any)',
            'Digital signature certificate'
        ],
        process: [
            'Calculate TDS liability',
            'Deduct TDS as per applicable rates',
            'Deposit TDS through challan 281',
            'Prepare quarterly TDS return',
            'Upload return on TRACES portal',
            'Download and verify Form 26AS',
            'Issue TDS certificates (16/16A)'
        ],
        timeline: 'Quarterly filing (by 31st of next month)',
        faqs: [
            {
                question: 'What are the different types of TDS returns?',
                answer: '24Q (salary), 26Q (non-salary payments to residents), 27Q (payments to non-residents), 27EQ (tax collected at source).'
            },
            {
                question: 'What is the penalty for late TDS filing?',
                answer: 'Late filing attracts a penalty of ₹200 per day until filed. If tax is deducted but not deposited, penalty is amount of TDS plus interest @1.5% per month.'
            }
        ]
    },

    'Professional Tax Registration': {
        description: `Professional Tax Registration is state-specific registration for businesses and professionals liable to pay professional tax. Employers must register and deduct professional tax from employee salaries above the threshold limit and remit it to the state government. Each state has different thresholds and tax slabs.`,
        benefits: [
            'Legal compliance with state PT laws',
            'Deduction allowed under Income Tax',
            'Avoid penalties and prosecution',
            'Required for company registration',
            'Employer statutory compliance',
            'Professional credibility',
            'Government tender requirement',
            'State-specific benefits'
        ],
        documents: [
            'Professional Tax enrollment application',
            'PAN Card of business',
            'Business registration certificate',
            'GST registration certificate',
            'List of employees',
            'Rent agreement/ownership proof',
            'Address proof of business',
            'Bank account details'
        ],
        process: [
            'Check PT applicability in your state',
            'Fill state-specific application form',
            'Submit required documents',
            'Pay registration/enrollment fee',
            'Receive PT registration certificate',
            'Deduct PT from employee salaries',
            'File monthly/annual PT returns'
        ],
        timeline: '10-15 working days',
        faqs: [
            {
                question: 'In which states is PT applicable?',
                answer: 'PT is applicable in most states except Delhi, Haryana, Uttarakhand, UP, Rajasthan, HP, Punjab, and J&K. Rates vary from ₹200-2,500 annually.'
            },
            {
                question: 'Is PT registration mandatory?',
                answer: 'Yes, if you\'re an employer and employee salaries exceed the prescribed limit, PT registration is mandatory. Professionals also need to register if income exceeds limits.'
            }
        ]
    },

    'Trademark Registration': {
        description: `Trademark Registration provides legal protection to your brand name, logo, or tagline under the Trade Marks Act, 1999. Register your trademark starting at ₹5,999 and get exclusive legal rights to use the mark. Registered trademarks prevent unauthorized usage by competitors, can be used with ® symbol, and are valid for 10 years (renewable indefinitely).`,
        pricing: {
            professionalFee: '₹1,499',
            govtFee: '₹4,500 (Individuals/MSMEs/Startups) or ₹9,000 (Others)',
            total: 'Starting from ₹5,999',
            timeline: '3 working days for application filing, 18-24 months for certificate',
            includes: [
                '30-minute consultation with a Trademark Expert',
                'Thorough Trademark Search to avoid objections',
                'Assistance in selecting the correct Trademark Class',
                'Preparation and filing of Form TM-A',
                'Complimentary Form 48 (Power of Attorney)',
                'TM Receipt issued within 3 working days'
            ]
        },
        benefits: [
            'Exclusive legal rights to use the trademark',
            'Protection against infringement - Legal action against unauthorized use',
            'Brand trust - Builds customer confidence and brand recognition',
            'Use of ™ and ® symbols - Professional brand identity',
            'Competitive advantage in the market',
            'Can be licensed or franchised for additional revenue',
            'Intellectual property asset with monetary value',
            'Valid for 10 years (renewable indefinitely)',
            'Nationwide protection across India'
        ],
        trademarkClasses: {
            total: 45,
            goodsClasses: 'Classes 1-34 for goods',
            servicesClasses: 'Classes 35-45 for services',
            classification: 'NICE Classification',
            note: 'Selection is based on the nature of the product/service and industry'
        },
        documentsRequired: {
            individuals: [
                'Identity proof (Aadhaar/PAN/Passport)',
                'Address proof (Utility bill/Bank statement)',
                'Logo (optional, if registering a logo)',
                'User Affidavit (for claiming prior use)'
            ],
            partnershipLlpCompany: [
                'Registration certificate',
                'Power of Attorney (Form 48)',
                'Identity proof of the signatory'
            ]
        },
        documents: [
            'Trademark logo/wordmark',
            'Applicant ID proof (Aadhaar/PAN/Passport)',
            'Business registration certificate',
            'Address proof of applicant',
            'Authorized signatory details',
            'POA (if filed through agent)',
            'User affidavit (for claiming prior use)',
            'Trademark search report'
        ],
        process: [
            'Trademark Search - Identifying unique mark and correct class to avoid conflicts',
            'Vienna Codification - For marks with visual/logo elements classification',
            'Filing Application - Submitting Form TM-A with all required documents',
            'Formalities Check - Initial verification by the trademark registry',
            'Examination - Checking for absolute/relative grounds of refusal',
            'Publication - Advertisement in the Trademark Journal for 4 months',
            'Opposition - Resolving any third-party claims (if raised)',
            'Registration - Issuance of the Trademark Certificate (valid for 10 years)'
        ],
        timeline: '18-24 months for certificate (3 days for filing)',
        trademarkTypes: [
            'Product Mark - For goods and products',
            'Service Mark - For services',
            'Certification Mark - Certifies quality/origin',
            'Collective Mark - Used by members of an association'
        ],
        faqs: [
            {
                question: 'How long does trademark registration take?',
                answer: 'While the application is filed within 3 working days, the complete registration process and certificate issuance usually takes 18-24 months due to government examination, publication, and opposition periods.'
            },
            {
                question: 'What is the validity of a trademark?',
                answer: 'A registered trademark is valid for 10 years from the date of filing and can be renewed indefinitely every 10 years by paying the renewal fees.'
            },
            {
                question: 'Can I use TM before registration?',
                answer: 'Yes, you can use the TM (™) symbol when your application is filed. After registration is complete, you can use the registered (®) symbol.'
            },
            {
                question: 'How much does trademark registration cost?',
                answer: 'Our professional fee is ₹1,499. Government fees are ₹4,500 for individuals/MSMEs/startups or ₹9,000 for others. Total cost starts from ₹5,999 including all charges.'
            },
            {
                question: 'What are trademark classes?',
                answer: 'There are 45 trademark classes under NICE Classification - Classes 1-34 for goods and Classes 35-45 for services. You need to select the appropriate class based on your product or service category.'
            },
            {
                question: 'Can I register a trademark for multiple classes?',
                answer: 'Yes, you can apply for trademark registration in multiple classes. However, you need to pay separate government fees for each class.'
            },
            {
                question: 'What happens if someone opposes my trademark?',
                answer: 'If opposition is filed during the 4-month publication period, you can file a counter-statement. The matter is then heard by the Trademark Registry, and a decision is made based on merits.'
            },
            {
                question: 'Is trademark search mandatory?',
                answer: 'While not legally mandatory, trademark search is highly recommended to check if similar marks exist. This helps avoid objections and rejection of your application.'
            }
        ]
    },

    'ISO 9001-2015': {
        description: `ISO 9001:2015 is an internationally recognized Quality Management System (QMS) certification that demonstrates your organization's commitment to quality products/services and customer satisfaction. It helps improve processes, reduce waste, and enhance business credibility for domestic and international operations.`,
        benefits: [
            'International recognition and credibility',
            'Improved operational efficiency',
            'Better customer satisfaction',
            'Required for government tenders',
            'Competitive advantage',
            'Process standardization',
            'Reduced waste and costs',
            'Global market access',
            'Continuous improvement culture',
            'Enhanced employee morale'
        ],
        documents: [
            'Company registration certificate',
            'Quality policy and objectives',
            'Organizational structure',
            'Process flowcharts',
            'Standard Operating Procedures (SOPs)',
            'Work instructions and forms',
            'Customer and supplier lists',
            'Calibration certificates',
            'Previous audit reports (if any)'
        ],
        process: [
            'Gap analysis and readiness assessment',
            'Quality Management System documentation',
            'Implementation of QMS processes',
            'Internal audit and management review',
            'Application to certification body',
            'Stage 1 audit (documentation review)',
            'Stage 2 audit (implementation audit)',
            'Certification and registration'
        ],
        timeline: '3-6 months',
        faqs: [
            {
                question: 'What is the validity of ISO 9001 certificate?',
                answer: 'ISO 9001 certificate is valid for 3 years. Annual surveillance audits are conducted to ensure continued compliance.'
            },
            {
                question: 'Can small businesses get ISO certified?',
                answer: 'Yes, ISO 9001 is applicable to organizations of all sizes. There are consultants who specialize in helping small businesses achieve certification.'
            }
        ]
    },

    'ITR Filing': {
        description: `Income Tax Return (ITR) Filing is mandatory for individuals and businesses earning above the taxable limit. Get your ITR filed starting at ₹499 with expert CA assistance. Timely ITR filing helps you claim refunds, apply for loans, get visas, and maintain a clean financial record. We provide same-day filing service for all ITR forms.`,
        pricing: {
            individual: '₹499 (ITR-1, ITR-4)',
            business: '₹1,999 (Proprietorship - ITR-3)',
            partnership: '₹4,999 (Partnership/LLP - ITR-5)',
            company: '₹9,999 (Private Limited - ITR-6)',
            timeline: 'Same day filing',
            includes: [
                'Expert CA consultation',
                'Income computation and tax calculation',
                'Form selection and preparation',
                'Online ITR filing',
                'ITR-V acknowledgment',
                'E-verification support',
                'Refund tracking assistance'
            ]
        },
        itrForms: {
            itr1: 'ITR-1 (Sahaj) - Salaried individuals - ₹499',
            itr2: 'ITR-2 - Multiple properties, capital gains - ₹1,999',
            itr3: 'ITR-3 - Business/Professional income - ₹1,999',
            itr4: 'ITR-4 (Sugam) - Presumptive income - ₹499',
            itr5: 'ITR-5 - Partnership/LLP - ₹4,999',
            itr6: 'ITR-6 - Companies - ₹9,999',
            itr7: 'ITR-7 - Trusts, political parties - ₹9,999'
        },
        benefits: [
            'Tax Refund - Claim refund of excess tax paid',
            'Loan Applications - Required for home loans, car loans, personal loans',
            'Visa Processing - Mandatory for visa applications to many countries',
            'Carry Forward Losses - Set off losses against future income',
            'Avoid Penalties - Prevent late filing penalties and interest',
            'Financial Proof - Serves as income proof for various purposes',
            'Clean Record - Maintain compliance with tax laws',
            'Quick Processing - Faster refunds with timely filing'
        ],
        whoShouldFile: [
            'Individuals with income exceeding ₹2.5 lakhs (₹3L for senior citizens)',
            'Individuals claiming tax refund',
            'Individuals with foreign assets or income',
            'Companies and firms (mandatory regardless of profit/loss)',
            'Individuals depositing ₹1 crore+ in bank accounts',
            'Anyone wanting to carry forward losses'
        ],
        documents: [
            'PAN Card and Aadhaar Card',
            'Form 16 (for salaried individuals)',
            'Form 26AS (Tax Credit Statement)',
            'Bank statements and interest certificates',
            'Capital gains statements (if applicable)',
            'House property details and home loan certificate',
            'Investment proofs (80C, 80D, etc.)',
            'Business books of accounts (for business income)',
            'TDS certificates (Form 16A)'
        ],
        process: [
            'Document Collection - Gather Form 16, investment proofs, bank statements',
            'Income Computation - Calculate total income from all sources',
            'Tax Calculation - Compute tax liability and deductions',
            'Form Selection - Choose appropriate ITR form',
            'Form Preparation - Fill ITR with accurate details',
            'Online Filing - Submit on income tax e-filing portal',
            'E-verification - Verify using Aadhaar OTP/Net Banking/DSC',
            'Acknowledgment - Download ITR-V for records'
        ],
        timeline: 'Same day filing',
        faqs: [
            {
                question: 'What is the last date for ITR filing?',
                answer: 'For individuals (non-audit), the due date is 31st July. For businesses requiring audit and companies, it is 31st October. Late filing attracts penalties of ₹5,000 (₹1,000 if income < ₹5 lakhs).'
            },
            {
                question: 'Which ITR form should I file?',
                answer: 'ITR-1 for salaried individuals with simple income. ITR-2 for capital gains or multiple properties. ITR-3 for business income. ITR-4 for presumptive income. Our CA will help you choose the right form.'
            },
            {
                question: 'Can I file ITR if my income is below taxable limit?',
                answer: 'Yes, you can file ITR voluntarily even if income is below the taxable limit. It is beneficial for claiming refunds, loan applications, and visa processing.'
            },
            {
                question: 'How long does it take to get ITR refund?',
                answer: 'After successful e-verification, ITR refunds are typically processed within 20-45 days and credited directly to your bank account.'
            },
            {
                question: 'What is e-verification?',
                answer: 'E-verification is the process of verifying your ITR electronically using Aadhaar OTP, Net Banking, or Digital Signature. It is mandatory for processing your return.'
            },
            {
                question: 'Can I revise my ITR after filing?',
                answer: 'Yes, you can file a revised return if you discover any mistake. Revised return can be filed before the end of the assessment year or before assessment completion.'
            },
            {
                question: 'What is the penalty for late ITR filing?',
                answer: 'Late filing penalty is ₹5,000 (₹1,000 if total income < ₹5 lakhs). Additionally, interest under section 234A is charged on tax due.'
            },
            {
                question: 'Do I need CA for ITR filing?',
                answer: 'While simple ITR-1 can be filed yourself, having a CA ensures accuracy, maximizes deductions, and prevents errors that could lead to notices or penalties.'
            }
        ]
    },
    'GST Calculator': {
        description: 'Free online tool to calculate GST (Goods and Service Tax) inclusive and exclusive amounts easily.',
        benefits: [
            'Instant calculation',
            'Exclusive & Inclusive modes',
            'Standard rates (5%, 12%, 18%, 28%)',
            'Error-free results'
        ],
        documents: [],
        process: [],
        faqs: []
    },

    // ===== NEW SERVICES (Placeholders - Content to be scraped later) =====

    // Business Setup - New
    'Public Limited Company': {
        description: `A Public Limited Company (PLC) is a type of business entity that offers shares to the general public and has limited liability protection. It is governed by the Companies Act, 2013 and is ideal for large businesses looking to raise substantial capital from the public through stock exchanges. PLCs can issue shares, debentures, and bonds to the general public, making them suitable for businesses with ambitious growth plans. The minimum requirement is 7 shareholders (no maximum limit), 3 directors, and at least one resident director who has stayed in India for 182+ days in the previous calendar year.`,
        benefits: [
            'Can raise massive capital by issuing shares and bonds to the general public',
            'Limited liability - shareholders\' personal assets are protected',
            'Enhanced credibility and public image attracts investors and partners',
            'Shares are freely transferable on stock exchanges providing liquidity',
            'Perpetual succession - company exists independent of its members',
            'Better access to financing with favorable loan terms from banks',
            'Attracts skilled professionals due to growth opportunities',
            'No maximum limit on shareholders or paid-up capital'
        ],
        documents: [
            'PAN and Aadhaar cards of all directors (self-attested)',
            'Identity proof - Voter ID, Driving License, Aadhaar, or Passport',
            'Address proof (electricity/water bill, bank statement - not older than 2 months)',
            'Latest passport-sized photographs of all directors',
            'Digital Signature Certificate (DSC) for all directors',
            'Director Identification Number (DIN) for all directors',
            'Proof of registered office address (utility bill, property tax)',
            'No Objection Certificate (NOC) from property owner',
            'Rent agreement or sale deed of office premises',
            'Memorandum of Association (MoA) and Articles of Association (AoA)'
        ],
        process: [
            'Obtain Digital Signature Certificate (DSC) for all proposed directors',
            'Apply for Director Identification Number (DIN) for each director',
            'Apply to ROC for unique company name reservation',
            'Draft Memorandum of Association (MoA) and Articles of Association (AoA)',
            'File SPICe+ form on MCA portal (covers incorporation, DIN, PAN, TAN, GST)',
            'Submit all required documents with the incorporation forms',
            'ROC verification and processing of application',
            'Receive Certificate of Incorporation with CIN (Corporate ID Number)',
            'File Form INC-20A (Declaration of business commencement)'
        ],
        timeline: '15-25 working days',
        faqs: [
            {
                question: 'What is the minimum capital required for a Public Limited Company?',
                answer: 'There is no minimum authorized capital requirement for a Public Limited Company in India as per the Companies (Amendment) Act, 2015. However, companies typically maintain adequate capital for credibility.'
            },
            {
                question: 'How many shareholders and directors are required?',
                answer: 'A Public Limited Company requires a minimum of 7 shareholders (no maximum limit) and a minimum of 3 directors. At least one director must be a resident of India (stayed 182+ days in previous year).'
            },
            {
                question: 'Can a Public Limited Company be listed on stock exchange?',
                answer: 'Yes, a Public Limited Company can get listed on stock exchanges like BSE or NSE by fulfilling SEBI regulations and listing requirements. However, registration as PLC does not automatically mean listing.'
            },
            {
                question: 'What is the difference between Public and Private Limited Company?',
                answer: 'A Public Limited Company can offer shares to the public, requires minimum 7 shareholders and 3 directors, and has freely transferable shares. A Private Limited Company restricts share transfer, requires only 2 shareholders and 2 directors, and cannot invite public subscription.'
            }
        ]
    },
    'Nidhi Company': {
        description: 'Nidhi Company is a type of NBFC that promotes thrift and savings among its members. It accepts deposits and lends only to its members.',
        benefits: ['Easy to form', 'Low regulatory compliance', 'Members-only operations', 'Promotes savings', 'Limited liability'],
        documents: ['Minimum 7 members required', 'PAN & Aadhaar of Directors', 'Address Proof', 'Registered Office Proof'],
        process: ['Name Approval', 'DSC & DIN', 'File SPICe+ Form', 'Obtain CoI', 'Apply for Nidhi Status'],
        timeline: '20-30 working days',
        faqs: []
    },
    'Producer Company': {
        description: 'Producer Company is a body corporate for farmer producers to collectively process, market, and sell their produce.',
        benefits: ['Collective bargaining power', 'Access to markets', 'Limited liability', 'Better prices for produce', 'Government subsidies'],
        documents: ['Minimum 10 producers', 'PAN & Aadhaar', 'Land ownership documents', 'Address Proof'],
        process: ['Name Approval', 'DSC & DIN', 'Draft MOA & AOA', 'File Registration', 'Obtain Certificate'],
        timeline: '15-20 working days',
        faqs: []
    },

    // Tax & Compliance - New
    'GST Amendment': {
        description: 'GST Amendment service helps you modify your GST registration details including business name, address, partners, or other particulars.',
        benefits: ['Update business details', 'Change principal place', 'Add/remove partners', 'Modify bank details'],
        documents: ['GST Certificate', 'Supporting documents for changes', 'Authorization letter'],
        process: ['Login to GST Portal', 'File Amendment Application', 'Upload Documents', 'Verification', 'Approval'],
        timeline: '7-15 working days',
        faqs: []
    },
    'GST Notice Response': {
        description: 'Professional assistance to respond to GST notices and handle scrutiny, audits, and assessments by GST authorities.',
        benefits: ['Expert guidance', 'Timely response', 'Proper documentation', 'Penalty minimization', 'Compliance restoration'],
        documents: ['GST Notice received', 'GST Returns', 'Related invoices', 'Bank statements'],
        process: ['Notice Analysis', 'Document Collection', 'Draft Response', 'File Reply', 'Follow-up'],
        timeline: 'As per notice deadline',
        faqs: []
    },
    'Business Tax Filing': {
        description: 'Complete income tax filing service for businesses including computation of income, deductions, and timely filing of returns.',
        benefits: ['Accurate computation', 'Maximum deductions', 'Timely filing', 'Audit support', 'Compliance assurance'],
        documents: ['Financial Statements', 'Bank Statements', 'GST Returns', 'TDS Certificates'],
        process: ['Document Collection', 'Income Computation', 'Tax Calculation', 'Return Filing', 'Acknowledgment'],
        timeline: '5-7 working days',
        faqs: []
    },
    'TAN Registration': {
        description: 'Tax Deduction Account Number (TAN) is mandatory for entities responsible for deducting or collecting tax at source.',
        benefits: ['Legal compliance', 'Required for TDS/TCS', 'Easy online process', 'Quick allotment'],
        documents: ['PAN of Entity', 'Address Proof', 'Identity Proof of Applicant'],
        process: ['Fill Form 49B', 'Submit Application', 'Verification', 'TAN Allotment'],
        timeline: '7-10 working days',
        faqs: []
    },

    // Labour Compliance - New
    'EPF Registration': {
        description: `EPF Registration is the process through which an establishment or employer registers with the Employees' Provident Fund Organisation (EPFO) to participate in the mandatory savings and pension scheme. It is a crucial social security initiative designed to help employees build a retirement corpus through regular contributions from both the employee and the employer. This registration ensures that businesses stay compliant with regulatory requirements while providing financial security to their workforce during retirement, medical emergencies, or unforeseen circumstances. Registration is mandatory for establishments with 20 or more employees.`,
        benefits: [
            'Enables seamless handling of statutory requirements and TDS deductions',
            'Provides a reliable financial safety net for employees and families',
            'PF accounts are portable and can be transferred when changing jobs',
            'Automatic enrollment in Employee Pension Scheme (EPS) for retirement',
            'Accumulated funds can be withdrawn for education, marriage, or home purchase',
            'Emergency partial withdrawals available for medical needs',
            'Tax benefits under Section 80C of Income Tax Act',
            'Interest earned on PF balance is tax-free up to specified limits'
        ],
        documents: [
            'Business Registration Certificate (COI, Partnership Deed, or Shop Act License)',
            'PAN Card of the business entity and all Directors/Partners',
            'Proof of Address (Utility bills, Rental agreement, or Property tax)',
            'Bank Account details (Cancelled cheque and Bank statement)',
            'Digital Signature Certificate (DSC) of the authorized signatory',
            'Comprehensive Employee List (Name, DOB, Father\'s Name, Aadhaar, PAN)',
            'Employment Details (Joining date and salary breakup)',
            'Specimen Signature on company letterhead',
            'Form 5A (Information regarding ownership of the business)'
        ],
        process: [
            'Visit Shram Suvidha Portal (USSP) and create organization account',
            'Review prerequisites for EPFO-ESIC registration',
            'Fill registration form with establishment details and nature of business',
            'Provide count and details of current employees',
            'Upload mandatory documents including PAN, address proof, cancelled cheque',
            'Authenticate application using Digital Signature Certificate',
            'Final review and submission of application to EPFO',
            'Department verification of documents and application',
            'Receive unique PF Establishment Code and registration number'
        ],
        timeline: '7-15 working days',
        faqs: [
            {
                question: 'Is EPF registration mandatory for all businesses?',
                answer: 'EPF registration is mandatory for establishments with 20 or more employees. However, smaller businesses can register voluntarily to provide benefits to their employees.'
            },
            {
                question: 'What is the contribution rate for EPF?',
                answer: 'Both employee and employer contribute 12% of the basic salary. Out of employer\'s 12%, 8.33% goes to EPS (Pension Scheme) and 3.67% to EPF.'
            },
            {
                question: 'What is the deadline to register for EPF?',
                answer: 'Businesses must register within one month of reaching the 20-employee threshold to avoid penalties and interest charges.'
            },
            {
                question: 'What happens if I don\'t register for EPF?',
                answer: 'Non-compliance can lead to heavy penalties (up to ₹5,000 per month), interest on delayed payments (12% per annum), and legal action including imprisonment.'
            }
        ]
    },
    'ESI Registration': {
        description: `ESI Registration is a mandatory requirement under the Employee State Insurance Act, 1948, for businesses with 10 or more employees (threshold varies by state, some require 20+). It is a self-financing social security and health insurance scheme managed by the ESIC (Employees' State Insurance Corporation). This scheme provides employees earning up to ₹21,000 per month with full medical care and financial assistance during periods of sickness, maternity, or employment-related injury. ESI is one of the most comprehensive social security schemes in India, covering medical expenses for the insured and their dependents.`,
        benefits: [
            'Comprehensive medical care for insured person and family members from day one',
            'Maternity benefits with paid leave and medical assistance during pregnancy',
            'Disability compensation for temporary or permanent workplace injuries',
            'Monthly pension for dependents in case of employee death due to job injury',
            'Reimbursement of funeral expenses up to ₹15,000',
            'Unemployment allowance (Atal Bimit Vyakti Kalyan Yojana) for involuntary job loss',
            'Confinement expenses for insured women outside ESI hospitals',
            'Vocational rehabilitation for permanently disabled employees'
        ],
        documents: [
            'Registration Certificate of the Business (Shops & Establishments Act or Companies Act)',
            'PAN Card of the business and all stakeholders',
            'Address Proof of the registered office (Lease deed or Property Tax)',
            'List of all employees with Aadhaar and bank details',
            'Bank Account information (Cancelled cheque or Bank statement)',
            'Monthly salary details of all eligible employees',
            'Nomination forms for each employee',
            'Digital Signature Certificate of authorized signatory'
        ],
        process: [
            'Register on ESIC portal/Shram Suvidha portal to create login credentials',
            'Complete Employer Registration Form with business and branch details',
            'Upload required identity, address, and business proofs',
            'Link with Common Registration (integrates with other labour licenses)',
            'Department reviews application and documentation',
            'Receive unique 17-digit ESI Registration Number',
            'Register employees to generate their ESI numbers and Pehchan Cards'
        ],
        timeline: '7-10 working days',
        faqs: [
            {
                question: 'Who is eligible for ESI benefits?',
                answer: 'Employees earning a gross salary of ₹21,000 per month or less are eligible. For persons with disabilities, the limit is ₹25,000 per month.'
            },
            {
                question: 'What are the ESI contribution rates?',
                answer: 'The employer contributes 3.25% and the employee contributes 0.75% of the wages, making a total contribution of 4% of wages.'
            },
            {
                question: 'Are temporary and contract workers covered under ESI?',
                answer: 'Yes, both temporary and contract workers are covered under ESI if they meet the wage criteria and work in a covered establishment.'
            },
            {
                question: 'Is ESI registration mandatory for IT companies?',
                answer: 'Yes, ESI registration is mandatory for IT companies if the employee count exceeds the threshold (10 or 20 depending on the state) and employees earn within the wage limit.'
            }
        ]
    },
    'Shops & Establishment': {
        description: 'Shops & Establishment Act registration is mandatory for all commercial establishments to regulate working conditions.',
        benefits: ['Legal recognition', 'Employee protection', 'Working hours regulation', 'Leave entitlements'],
        documents: ['PAN & Aadhaar', 'Rent Agreement', 'Address Proof', 'Employee Count'],
        process: ['Application Filing', 'Document Verification', 'Inspection (if required)', 'Certificate Issuance'],
        timeline: '7-15 working days',
        faqs: []
    },
    'Labour License': {
        description: 'Labour License is required for establishments engaging contract workers under the Contract Labour Act.',
        benefits: ['Legal compliance', 'Contract worker management', 'Avoid penalties', 'Smooth operations'],
        documents: ['Company Registration', 'PAN', 'Contract Details', 'Worker Information'],
        process: ['Application Filing', 'Document Submission', 'Verification', 'License Issuance'],
        timeline: '15-30 working days',
        faqs: []
    },

    // Licenses & Registrations - New
    'ICEGATE Registration': {
        description: 'ICEGATE is the e-commerce portal of Indian Customs for online filing of import/export documents.',
        benefits: ['Online customs filing', 'Faster clearance', 'Track shipments', 'Digital documentation'],
        documents: ['IEC Code', 'PAN', 'Digital Signature', 'Business Registration'],
        process: ['Online Application', 'Document Upload', 'Verification', 'Registration Approval'],
        timeline: '3-5 working days',
        faqs: []
    },
    'Drug License': {
        description: 'Drug License is mandatory for manufacturing, selling, or distributing pharmaceutical products in India.',
        benefits: ['Legal authorization', 'Business credibility', 'Market access', 'Regulatory compliance'],
        documents: ['Business Registration', 'Pharmacist Certificate', 'Premises Layout', 'Equipment List'],
        process: ['Application Filing', 'Document Verification', 'Premises Inspection', 'License Issuance'],
        timeline: '30-60 working days',
        faqs: []
    },
    'Fire License': {
        description: 'Fire License/NOC is required for commercial establishments to ensure fire safety compliance.',
        benefits: ['Safety compliance', 'Insurance validity', 'Legal requirement', 'Business continuity'],
        documents: ['Building Plan', 'Fire Safety Equipment Details', 'Occupancy Certificate'],
        process: ['Application', 'Document Submission', 'Site Inspection', 'NOC Issuance'],
        timeline: '15-30 working days',
        faqs: []
    },
    'Barcode Registration': {
        description: 'Barcode registration with GS1 India provides unique product identification for retail and supply chain management.',
        benefits: ['Product identification', 'Inventory management', 'Retail compatibility', 'Export readiness'],
        documents: ['Business Registration', 'PAN', 'Product Details', 'Brand Authorization'],
        process: ['GS1 Application', 'Payment', 'Verification', 'Barcode Allotment'],
        timeline: '5-7 working days',
        faqs: []
    },
    'BIS Registration': {
        description: 'BIS (Bureau of Indian Standards) registration certifies that products meet Indian quality standards.',
        benefits: ['Quality certification', 'Market access', 'Consumer trust', 'Export compliance'],
        documents: ['Product Specifications', 'Test Reports', 'Factory License', 'Manufacturing Process'],
        process: ['Application', 'Factory Audit', 'Product Testing', 'Certificate Issuance'],
        timeline: '60-90 working days',
        faqs: []
    },
    'APEDA Registration': {
        description: 'APEDA registration is required for export of agricultural and processed food products from India.',
        benefits: ['Export authorization', 'Government subsidies', 'Market access', 'Trade facilitation'],
        documents: ['IEC Code', 'RCMC', 'Business Registration', 'Product Details'],
        process: ['Online Application', 'Document Upload', 'Verification', 'Registration Certificate'],
        timeline: '7-10 working days',
        faqs: []
    },

    // Trademark & IP - New
    'Trademark Search': {
        description: 'Comprehensive trademark search to check availability and avoid conflicts with existing trademarks.',
        benefits: ['Avoid conflicts', 'Risk assessment', 'Strategic planning', 'Save time and money'],
        documents: ['Proposed trademark name/logo', 'Class of goods/services'],
        process: ['Database Search', 'Conflict Analysis', 'Report Generation', 'Recommendations'],
        timeline: '1-2 working days',
        faqs: []
    },
    'Trademark Objection': {
        description: 'Professional response to trademark objections raised by the Registrar during examination.',
        benefits: ['Expert drafting', 'Higher success rate', 'Timely response', 'Legal compliance'],
        documents: ['Objection Notice', 'Trademark Application', 'Evidence of Use'],
        process: ['Objection Analysis', 'Draft Response', 'Filing', 'Hearing (if required)'],
        timeline: '30 days from notice',
        faqs: []
    },
    'Trademark Renewal': {
        description: 'Renew your trademark registration before expiry to maintain protection and rights.',
        benefits: ['Continued protection', 'Maintain rights', 'Avoid re-registration', 'Brand security'],
        documents: ['Trademark Certificate', 'Power of Attorney', 'Identity Proof'],
        process: ['Application Filing', 'Fee Payment', 'Verification', 'Renewal Certificate'],
        timeline: '3-6 months before expiry',
        faqs: []
    },
    'Trademark Assignment': {
        description: 'Transfer trademark ownership from one entity to another through legal assignment.',
        benefits: ['Legal transfer', 'Rights protection', 'Business continuity', 'Documented ownership'],
        documents: ['Assignment Deed', 'Trademark Certificate', 'Identity Proofs', 'NOC'],
        process: ['Draft Agreement', 'File TM-P', 'Verification', 'Updated Records'],
        timeline: '6-12 months',
        faqs: []
    },
    'Copyright Registration': {
        description: `Copyright registration is a legal process that grants creators exclusive rights over their original works—be they literary, musical, artistic, or software. In India, this protection is governed by the Copyright Act, 1957, which ensures that an individual or organization holding copyright can control the reproduction, distribution, adaptation, and public performance of their work. The process is administered by the Registrar of Copyrights and provides a legal framework to protect the expression of ideas in a tangible form. Beyond protection, registration offers additional benefits like legal remedies against infringement, eligibility for commercial licensing, and international recognition under treaties like the Berne Convention.`,
        benefits: [
            'Provides strong legal protection as prima facie evidence of ownership in court',
            'Grants exclusive control over reproduction, distribution, performance, and adaptation',
            'Creates official government record establishing authorship',
            'Acts as deterrent against infringement with legal enforceability',
            'Simplifies process of initiating legal proceedings and claiming statutory damages',
            'Enables creators to license, sell, or transfer work for monetization',
            'Aligns with international standards (Berne Convention) for global recognition',
            'Protection lasts for lifetime plus 60 years after author\'s death'
        ],
        documents: [
            'Two copies of the work (physical or digital)',
            'Proof of Identity for author, owner, or authorized agent',
            'No-Objection Certificate (NOC) if applicant is not the author',
            'Power of Attorney if agent is filing on behalf of creator',
            'Source Code (for software) - First and last 10 pages',
            'Documents confirming ownership if transferred from original author',
            'Photographs of artistic works or screenshots of digital works'
        ],
        process: [
            'Prepare all necessary documents and fill out Form XIV',
            'File application online via Copyright Office portal',
            'Receive Diary Number (unique tracking ID)',
            'Mandatory 30-day waiting period for third-party objections',
            'Scrutiny by Registrar to ensure compliance and originality',
            'Respond to any discrepancies or objections raised',
            'Issuance of Copyright Registration Certificate'
        ],
        timeline: '6-12 months (includes 30-day mandatory waiting period)',
        faqs: [
            {
                question: 'What works are eligible for copyright protection?',
                answer: 'Literary works (books, software, articles), artistic works (paintings, logos, photographs), musical works (compositions, lyrics), cinematograph films, and sound recordings are eligible for copyright protection.'
            },
            {
                question: 'Is copyright registration mandatory in India?',
                answer: 'No, copyright exists automatically upon creation. However, registration is highly recommended as it serves as prima facie evidence in court and is essential for taking legal action and claiming damages.'
            },
            {
                question: 'What is the difference between copyright and trademark?',
                answer: 'Copyright protects creative expressions and original works (books, music, art, software), while trademarks protect brand identifiers like names, logos, and slogans used in commerce.'
            },
            {
                question: 'How long does copyright protection last?',
                answer: 'For original works, copyright lasts for the lifetime of the author plus 60 years after death. For anonymous works and works of organizations, it lasts 60 years from publication date.'
            }
        ]
    },
    'Patent Registration': {
        description: `Patent registration is a legal process under intellectual property law that provides an inventor with exclusive rights over their invention. It restricts third parties from making, using, selling, or importing the invention without authorization for a specified period, typically 20 years. In India, patents are regulated by the Indian Patent Act, 1970. To be patentable, an invention must be novel (new), involve an inventive step (non-obvious to a skilled person), and have industrial applicability. This applies to various sectors including engineering, chemicals, pharmaceuticals, biotechnology, and software (with technical contribution).`,
        benefits: [
            'Provides 20-year monopoly on commercial use of the invention',
            'Enables revenue generation through licensing or selling technology',
            'Establishes business as an innovation leader with competitive advantage',
            'Creates valuable intangible assets that increase company valuation',
            'Enhances credibility when seeking funding from investors',
            'Enables legal action including injunctions and damages against infringers',
            'Can be used as collateral for securing business loans',
            'Provides recognition and prestige to inventors'
        ],
        documents: [
            'Form 1: Application for grant of patent',
            'Form 2: Provisional or complete specification (technical details, drawings, claims)',
            'Form 3: Statement regarding foreign applications (if any)',
            'Form 5: Declaration of inventorship',
            'Form 26: Power of Attorney (if filed through agent)',
            'Priority Documents (if claiming priority from foreign filing)',
            'Detailed drawings and diagrams of the invention',
            'Proof of right to file (if applicant is not the inventor)'
        ],
        process: [
            'Conduct novelty/patent search to ensure invention hasn\'t been disclosed before',
            'Prepare technical specifications (Provisional or Complete)',
            'Submit Form 1 and Form 2 to Indian Patent Office',
            'Application published in Patent Journal (after 18 months or early publication)',
            'File Request for Examination (Form 18) to initiate examination',
            'Receive and respond to First Examination Report (FER)',
            'Address technical and legal objections raised by examiner',
            'Grant of patent and issuance of certificate'
        ],
        timeline: '1-3 years (from filing to grant)',
        faqs: [
            {
                question: 'What cannot be patented in India?',
                answer: 'Discoveries of scientific principles, abstract theories, mathematical methods, surgical/medical methods, inventions contrary to public morality, and traditional knowledge cannot be patented in India.'
            },
            {
                question: 'Can software be patented in India?',
                answer: 'Software per se is not patentable. However, software that has a "technical contribution" or is used in conjunction with hardware to produce a technical effect may be patentable.'
            },
            {
                question: 'What is a Provisional Patent Application?',
                answer: 'A provisional application is a temporary filing that secures a priority date while the invention is still under development. A complete specification must be filed within 12 months of the provisional filing.'
            },
            {
                question: 'How long is a patent valid in India?',
                answer: 'Patents in India are valid for 20 years from the date of filing, subject to annual maintenance fee payments. After 20 years, the invention enters the public domain.'
            }
        ]
    },
    'Patent Search': {
        description: 'Comprehensive patent search to assess patentability and identify prior art.',
        benefits: ['Assess novelty', 'Identify competition', 'Strategic planning', 'Cost savings'],
        documents: ['Invention description', 'Technical specifications'],
        process: ['Database Search', 'Prior Art Analysis', 'Report Generation'],
        timeline: '5-7 working days',
        faqs: []
    },
    'Design Registration': {
        description: 'Protect the unique visual appearance of your products through design registration.',
        benefits: ['Visual protection', '10+ years validity', 'Commercial rights', 'Legal remedies'],
        documents: ['Design Representations', 'Product Details', 'Creator Information'],
        process: ['Application Filing', 'Examination', 'Publication', 'Registration'],
        timeline: '6-12 months',
        faqs: []
    },
    'Logo Designing': {
        description: 'Professional logo design services to create unique brand identity for your business.',
        benefits: ['Unique identity', 'Professional design', 'Multiple concepts', 'Brand consistency'],
        documents: ['Brand Brief', 'Preferences', 'Industry Information'],
        process: ['Brief Discussion', 'Concept Creation', 'Revisions', 'Final Delivery'],
        timeline: '5-7 working days',
        faqs: []
    },

    // ISO & Certifications - New
    'FSSAI License': {
        description: `FSSAI License is mandatory for food businesses in India under the Food Safety and Standards Authority of India. There are three types: Basic Registration (turnover < ₹12 lakhs), State License (₹12 lakhs - ₹20 crores), and Central License (> ₹20 crores or for importers/exporters). All food business operators including manufacturers, processors, storage units, restaurants, caterers, and online food sellers must obtain appropriate FSSAI registration. The license is valid for 1-5 years and ensures compliance with food safety standards.`,
        benefits: [
            'Legal authorization to operate food business in India',
            'Display 14-digit FSSAI number on products for consumer trust',
            'Required for listing on Swiggy, Zomato, and other platforms',
            'Access to government and institutional markets',
            'Proof of compliance with food safety standards',
            'Enhanced brand credibility and customer confidence',
            'Protection from legal penalties up to ₹5 lakhs',
            'Enables food import/export with Central License'
        ],
        documents: [
            'Photo ID of Food Business Operator (Aadhaar, PAN, Passport)',
            'Address proof of business premises',
            'Business registration certificate or partnership deed',
            'List of food products to be manufactured/sold',
            'Food Safety Management Plan (for State/Central License)',
            'Layout plan of processing unit (for manufacturers)',
            'List of equipment and machinery with capacity',
            'NOC from municipality or health department (if applicable)'
        ],
        process: [
            'Determine license type based on turnover and operations',
            'Register on FoSCoS portal (foscos.fssai.gov.in)',
            'Fill Form A (Basic) or Form B (State/Central) with business details',
            'Upload required documents in prescribed format',
            'Pay applicable fees online (varies by license type and validity)',
            'Submit application for review',
            'Inspection by food safety officer (for State/Central)',
            'Receive FSSAI certificate with 14-digit license number'
        ],
        timeline: '7 days (Basic), 30-60 days (State/Central)',
        faqs: [
            {
                question: 'Which FSSAI license type do I need?',
                answer: 'Basic Registration for turnover < ₹12 lakhs, State License for ₹12 lakhs to ₹20 crores, Central License for turnover > ₹20 crores or if you import/export food products.'
            },
            {
                question: 'Is FSSAI mandatory for cloud kitchens and home bakers?',
                answer: 'Yes, all food businesses including cloud kitchens, home bakers, tiffin services, and online food sellers must obtain at least FSSAI Basic Registration.'
            },
            {
                question: 'What is the penalty for operating without FSSAI?',
                answer: 'Operating without FSSAI license can attract penalty up to ₹5 lakhs and/or imprisonment up to 6 months. The business can also be shut down.'
            },
            {
                question: 'How do I renew my FSSAI license?',
                answer: 'Apply for renewal 30-180 days before expiry through FoSCoS portal. Late renewal attracts ₹100/day penalty for State/Central Licenses.'
            }
        ]
    },

    // MCA Compliance - New
    'Company Compliance': {
        description: `Annual compliance for Private Limited Companies involves a set of mandatory filings and meetings required under the Companies Act, 2013 to maintain legal standing and avoid penalties. Key requirements include conducting minimum 4 board meetings per year, holding Annual General Meeting (AGM) within 6 months of financial year end, and filing Form AOC-4 (financial statements) and Form MGT-7 (annual return) with ROC. Non-compliance can result in penalties of ₹100 per day per form, director disqualification, and potential striking off of the company.`,
        benefits: [
            'Maintain active company status with Registrar of Companies',
            'Avoid heavy penalties (₹100/day/form for delayed filing)',
            'Prevent director disqualification and legal consequences',
            'Build credibility with banks, investors, and partners',
            'Ensure smooth business operations and contract execution',
            'Meet statutory requirements under Companies Act, 2013',
            'Expert handling ensures accurate and timely filings',
            'Comprehensive compliance reducing stress on management'
        ],
        documents: [
            'Financial Statements (Balance Sheet, P&L, Cash Flow)',
            'Auditor\'s Report from qualified Chartered Accountant',
            'Board Resolutions for approval of financials',
            'Director Details and DIN information',
            'Shareholding Pattern and register of members',
            'Minutes of Board Meetings and AGM',
            'Form DIR-12 (if any director changes)',
            'Digital Signature Certificate of authorized signatory'
        ],
        process: [
            'Conduct minimum 4 Board Meetings (gap max 120 days)',
            'Prepare and finalize financial statements',
            'Get financial statements audited by CA',
            'Hold Annual General Meeting within 6 months of FY end',
            'File Form AOC-4 within 30 days of AGM',
            'File Form MGT-7/MGT-7A within 60 days of AGM',
            'File DIR-3 KYC for all directors by September 30th',
            'File ITR-6 (Company Income Tax Return)'
        ],
        timeline: 'Annual cycle (filings due after AGM)',
        faqs: [
            {
                question: 'What are the penalties for late filing of AOC-4 and MGT-7?',
                answer: 'The penalty is ₹100 per day for each form filed late. This can accumulate to significant amounts if compliance is delayed for months. Additionally, prolonged non-compliance can lead to director disqualification.'
            },
            {
                question: 'When should the AGM be held?',
                answer: 'For the first AGM, it should be held within 9 months from the first financial year end. For subsequent years, the AGM must be held within 6 months from the end of the financial year (September 30th for March year-end).'
            },
            {
                question: 'What is the difference between Form AOC-4 and MGT-7?',
                answer: 'Form AOC-4 is for filing financial statements (balance sheet, P&L, audit report) within 30 days of AGM. Form MGT-7 is the annual return containing shareholding pattern, director info, and company activities, filed within 60 days of AGM.'
            },
            {
                question: 'Is DIR-3 KYC mandatory every year?',
                answer: 'Yes, every director who has been allotted a DIN must file DIR-3 KYC annually by September 30th. Failure to file results in a penalty of ₹5,000 and DIN deactivation until compliance.'
            }
        ]
    },
    'LLP Compliance': {
        description: `Annual compliance for Limited Liability Partnerships (LLPs) in India involves mandatory filings under the LLP Act, 2008, primarily Form 11 (Annual Return) and Form 8 (Statement of Account & Solvency). Form 11 must be filed by May 30th (within 60 days of FY end) providing details of partners and their contributions. Form 8 must be filed by October 30th (within 30 days from 6 months of FY end) reporting the LLP's financial position. Non-compliance attracts ₹100 per day penalty per form with no upper limit.`,
        benefits: [
            'Maintain active LLP status with Registrar of Companies',
            'Avoid heavy penalties (₹100/day/form with no cap)',
            'Prevent LLP from being struck off for non-compliance',
            'Lower compliance burden compared to Private Limited Companies',
            'No requirement of AGM or detailed board meetings',
            'Expert handling ensures accurate and timely filings',
            'Complete package covering all annual statutory requirements',
            'Includes ITR-5 filing assistance'
        ],
        documents: [
            'Statement of Account & Solvency (financial statements)',
            'Partner contribution details and any changes',
            'LLP Registration Certificate and PAN',
            'Bank statements for the financial year',
            'Details of designated partners with DIN',
            'Digital Signature Certificate of designated partner',
            'Income and expense details for ITR filing',
            'Copy of partnership agreement (if amended)'
        ],
        process: [
            'Prepare and finalize financial statements',
            'Get accounts certified by CA (if turnover > ₹40L or contribution > ₹25L)',
            'File Form 11 (Annual Return) by May 30th',
            'File Form 8 (Statement of Account & Solvency) by October 30th',
            'File Income Tax Return (ITR-5) by July 31st/Sept 30th',
            'Maintain proper books of accounts at registered office'
        ],
        timeline: 'Annual cycle (Form 11 by May 30, Form 8 by Oct 30)',
        faqs: [
            {
                question: 'What is the penalty for late filing of Form 11 and Form 8?',
                answer: 'The penalty is ₹100 per day for each form filed late, with no upper limit. This can accumulate to significant amounts if compliance is delayed for months.'
            },
            {
                question: 'Is Form 8 certification by CA mandatory?',
                answer: 'Form 8 must be certified by a practicing CA, CS, or CWA. However, tax audit is mandatory only if the LLP\'s annual turnover exceeds ₹40 lakh or partner contribution exceeds ₹25 lakh.'
            },
            {
                question: 'Can a newly incorporated LLP skip Form 11 for the first year?',
                answer: 'If an LLP was incorporated on or after October 1st of a financial year, it may extend its first financial year or filing may be optional for that initial period.'
            },
            {
                question: 'What is the difference between Form 11 and Form 8?',
                answer: 'Form 11 is the Annual Return containing partner details, contributions, and changes. Form 8 is the Statement of Account & Solvency containing financial position (assets, liabilities) and solvency declaration.'
            }
        ]
    },
    'OPC Compliance': {
        description: 'Annual compliance package for One Person Companies with simplified requirements.',
        benefits: ['Reduced compliance', 'Single director meeting', 'Simplified filings', 'Cost effective'],
        documents: ['Financial Statements', 'Director Details', 'Bank Statements'],
        process: ['Financial Closure', 'Board Resolution', 'Annual Filings'],
        timeline: 'Annual cycle',
        faqs: []
    },
    'Name Change': {
        description: 'Change your company or LLP name through proper MCA procedure.',
        benefits: ['Rebranding', 'Legal process', 'Updated documents', 'Fresh start'],
        documents: ['Board Resolution', 'NOC from Existing Name', 'Proposed Names'],
        process: ['Name Approval', 'EGM', 'File Forms', 'New Certificate'],
        timeline: '15-30 working days',
        faqs: []
    },
    'Registered Office Change': {
        description: 'Change your company registered office address within same state or to different state.',
        benefits: ['Updated records', 'Legal compliance', 'Business relocation', 'Proper documentation'],
        documents: ['Board Resolution', 'New Address Proof', 'NOC from Owner'],
        process: ['Board Meeting', 'File INC-22', 'Certificate Update'],
        timeline: '7-30 working days',
        faqs: []
    },
    'Director Change': {
        description: 'Add or remove directors from your company with proper MCA filings.',
        benefits: ['Board restructuring', 'Legal compliance', 'Updated records', 'Governance'],
        documents: ['Board Resolution', 'New Director Details', 'Consent Letter', 'DIN'],
        process: ['Board Meeting', 'File DIR-12', 'Update Records'],
        timeline: '7-15 working days',
        faqs: []
    },
    'Share Transfer': {
        description: 'Transfer company shares from one shareholder to another with proper documentation.',
        benefits: ['Ownership transfer', 'Legal documentation', 'Updated records', 'Valuation'],
        documents: ['Share Transfer Deed', 'Board Resolution', 'Share Certificates'],
        process: ['Valuation', 'Transfer Deed', 'Board Approval', 'Register Update'],
        timeline: '7-15 working days',
        faqs: []
    },
    'DIN eKYC': {
        description: 'Annual KYC update for Directors to maintain active DIN status.',
        benefits: ['Maintain DIN', 'Avoid deactivation', 'Compliance', 'Simple process'],
        documents: ['PAN', 'Aadhaar', 'Mobile & Email'],
        process: ['Login to MCA', 'Submit DIR-3 KYC', 'OTP Verification'],
        timeline: 'Before 30th September annually',
        faqs: []
    },
    'INC-20A Filing': {
        description: 'Declaration of commencement of business within 180 days of incorporation.',
        benefits: ['Start business operations', 'Avoid penalties', 'Compliance', 'Legal requirement'],
        documents: ['Bank Subscription Proof', 'Registered Office Verification'],
        process: ['Verify Subscription', 'File Declaration', 'Certificate'],
        timeline: 'Within 180 days of incorporation',
        faqs: []
    },
    'LLP Winding Up': {
        description: 'Close your LLP through voluntary winding up procedure.',
        benefits: ['Legal closure', 'Liability settlement', 'Partner exit', 'Clear records'],
        documents: ['Partner Consent', 'Financial Statements', 'NOCs', 'Indemnity Bond'],
        process: ['Partner Resolution', 'File Form 24', 'Strike Off'],
        timeline: '3-6 months',
        faqs: []
    },
    'Company Winding Up': {
        description: 'Close your company through voluntary winding up or strike off procedure.',
        benefits: ['Legal closure', 'Director relief', 'Asset distribution', 'Clear records'],
        documents: ['Board Resolution', 'Special Resolution', 'Affidavit', 'NOCs'],
        process: ['Board Meeting', 'EGM', 'File STK-2', 'Strike Off'],
        timeline: '3-6 months',
        faqs: []
    }
};

// Export service images mapping
export const serviceImages = {
    'Proprietorship': '/img/proprietorship.png',
    'Partnership': '/img/Partnership-Registration.png',
    'One Person Company': '/img/opc.webp',
    'Limited Liability Partnership': '/img/LLP.png',
    'Private Limited Company': '/img/creg.png',
    'Trust Registration': '/img/Trust.png',
    'Society Registration': '/img/Trust.png',
    'Digital Signature': '/img/digital-signature-india.png',
    'Udyam Registration': '/img/UDYAM-Registration.webp',
    'Startup India Registration': '/img/startup.png',
    'FSSAI Registration': '/img/FSSAI_Registration.png',
    'Professional Tax': '/img/Professional_Tax.png',
    'Darpan Registration': '/img/darpan-registration.png',
    'RCMC Registration': '/img/RCMC-Registration.jpg',
    'Halal Certificate': '/img/Halal-Certificate.jpg',
    'Import Export Code': '/img/import_export_india.png',
    'Trade License': '/img/Trade-License.png',
    'GST Registration': '/img/GST-Registration-software-india.jpg',
    'GST Filing': '/img/gst1.png',
    'LUT Filing Under GST': '/img/gst2.png',
    'ITR Filing': '/img/income-Tax-e-Filing-india.png',
    'GST Cancellation': '/img/GST-Notice.jpg',
    'Accounting': '/img/account.jpeg',
    'TDS Return Filing': '/img/tds-return-filing-india.png',
    'Professional Tax Registration': '/img/Professional_Tax.png',
    'Trademark Registration': '/img/Trademark.webp',
    'ISO 9001-2015': '/img/iso.png',
};
